"use client"

import { useState, useEffect, useRef } from "react"
import { 
  Upload, Eye, ChevronLeft, X, AlertCircle, CheckCircle, Download, Search,
  Package, Truck, PlayCircle, Send, MapPin, Edit, RotateCcw,
  FileText, Calendar, User, Hash, Box, Plus, Trash2,
  RefreshCw, Image as ImageIcon, FileSpreadsheet, File, Globe, Code, Info, Bug
} from 'lucide-react'
import Papa from 'papaparse'
import { authFetch } from "../utils/authFetch";

const OrderManagement = ({ token, user, vistaPedidos = 'compra_venta' }) => {
  const isAdmin = user?.rol === 'admin';
  const API_URL = process.env.REACT_APP_API_URL
    || (window.location.hostname === 'localhost'
      ? 'http://localhost:3001/api'
      : 'https://alvarezbohlback-production.up.railway.app/api')

  // Estados
  const [activeTab, setActiveTab] = useState(vistaPedidos === 'placa' ? "picking_salida" : "venta_mercaderia")
  const [currentView, setCurrentView] = useState("list")

  useEffect(() => {
    if (vistaPedidos === 'placa') {
      if (activeTab !== 'picking_salida' && activeTab !== 'devolucion_salida') {
        setActiveTab("picking_salida")
      }
    } else if (activeTab === 'picking_salida' || activeTab === 'devolucion_salida') {
      setActiveTab("venta_mercaderia")
    }
    setCurrentView("list")
    setSelectedOrder(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaPedidos])
  const [orders, setOrders] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [previewData, setPreviewData] = useState(null)

  // CSV Upload
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [csvFile, setCsvFile] = useState(null)
  const [parsedCSV, setParsedCSV] = useState(null)
  const [uploadLoading, setUploadLoading] = useState(false)

  // Filtros
  const [searchTerm, setSearchTerm] = useState("")
  const [filterEstado, setFilterEstado] = useState("")
  const [filterFechaDesde, setFilterFechaDesde] = useState("")
  const [filterFechaHasta, setFilterFechaHasta] = useState("")

  // Paginación
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 20

  // Admin: selección masiva para eliminar
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set())
  const [deletingBulk, setDeletingBulk] = useState(false)
  const [deletingRowId, setDeletingRowId] = useState(null)

  // Loading states
  const [loadingLine, setLoadingLine] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewPicking, setPreviewPicking] = useState(null)
  const [selectedLineForPreview, setSelectedLineForPreview] = useState(null)

  // Reubicación manual
  const [modoReubicacion, setModoReubicacion] = useState("automatico")
  const [reubicacionesManuales, setReubicacionesManuales] = useState({})

  // Modal de retorno
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [selectedLineForReturn, setSelectedLineForReturn] = useState(null)
  const [returnData, setReturnData] = useState({
    motivo: '',
    estado_producto: 'bueno'
  })
  const [loadingReturn, setLoadingReturn] = useState(false)

  // Modal de exportación
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportTipoLoading, setExportTipoLoading] = useState(false)

  // Modal de procesar devolución (multi-step)
  const [showRetornoModal, setShowRetornoModal] = useState(false)
  const [retornoModalStep, setRetornoModalStep] = useState(1)
  const [retornoModalData, setRetornoModalData] = useState({
    lineaId: null,
    returnId: null,
    productCode: '',
    productDesc: '',
    lote: '',
    fechaVencimiento: '',
    estadoProducto: '',
    ubicacion: '',
    fefoRecommendation: null,
    cantidades: { cajas: 0, displays: 0, unidades: 0 }
  })
  const [retornoModalLoading, setRetornoModalLoading] = useState(false)

  // 🆕 Modal de completar entrada
  const [showCompleteEntradaModal, setShowCompleteEntradaModal] = useState(false)
  const [selectedLineForComplete, setSelectedLineForComplete] = useState(null)
  const [entradaLotes, setEntradaLotes] = useState([
    { id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }
  ])
  const [entradaSubmitProgress, setEntradaSubmitProgress] = useState(null)
  const [loadingComplete, setLoadingComplete] = useState(false)

  // 🆕 Modal de completar salida
  const [showCompleteSalidaModal, setShowCompleteSalidaModal] = useState(false)
  const [lotesFefoParaSalida, setLotesFefoParaSalida] = useState([]) // ✅ NUEVO: Almacenar lotes FEFO


  const fileInputRef = useRef(null)

  // Auto-sync AB
  const [syncStatus, setSyncStatus] = useState({ loading: false, nuevos: 0, data: null })
  const [syncProgress, setSyncProgress] = useState(null) // { phase, current, total, exitosos, errores, message }

  // Debug Raw AB
  const [debugRawData, setDebugRawData] = useState(null)
  const [debugLoading, setDebugLoading] = useState(false)
  const [showDebugPanel, setShowDebugPanel] = useState(false)



  useEffect(() => {
    loadOrders()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterEstado, filterFechaDesde, filterFechaHasta])

  // Limpiar selección al cambiar de tab o filtros
  useEffect(() => {
    setSelectedOrderIds(new Set())
  }, [activeTab, filterEstado, filterFechaDesde, filterFechaHasta, searchTerm])

  const handleToggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  const handleToggleAllVisible = () => {
    const visibleIds = getPaginatedOrders().paginated.map(o => o.id)
    const allSelected = visibleIds.every(id => selectedOrderIds.has(id))
    setSelectedOrderIds(prev => {
      const next = new Set(prev)
      if (allSelected) {
        visibleIds.forEach(id => next.delete(id))
      } else {
        visibleIds.forEach(id => next.add(id))
      }
      return next
    })
  }

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(
      `¿Eliminar el pedido ${order.codigo_pedido}?\n\n` +
      `Estado: ${order.estado}\n\n` +
      `Esta acción NO revierte movimientos de stock ni lotes ya procesados. Es irreversible.`
    )) return

    setDeletingRowId(order.id)
    try {
      const response = await authFetch(`${API_URL}/orders/${order.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response) return
      const result = await response.json()
      if (result.success) {
        alert(`✅ Pedido ${order.codigo_pedido} eliminado`)
        setSelectedOrderIds(prev => {
          const next = new Set(prev)
          next.delete(order.id)
          return next
        })
        await loadOrders()
      } else {
        alert(`❌ Error: ${result.error || 'No se pudo eliminar'}`)
      }
    } catch (err) {
      console.error('Error eliminando pedido:', err)
      alert(`❌ Error al eliminar: ${err.message}`)
    } finally {
      setDeletingRowId(null)
    }
  }

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedOrderIds)
    if (ids.length === 0) return

    if (!window.confirm(
      `¿Eliminar ${ids.length} pedido(s) seleccionado(s)?\n\n` +
      `Esta acción NO revierte movimientos de stock ni lotes ya procesados. Es irreversible.`
    )) return

    if (!window.confirm(`Confirmación final: se eliminarán ${ids.length} pedidos. ¿Continuar?`)) return

    setDeletingBulk(true)
    try {
      const response = await authFetch(`${API_URL}/orders/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      })
      if (!response) return
      const result = await response.json()
      if (result.success) {
        alert(`✅ ${result.deleted_count} pedido(s) eliminado(s)${result.not_found_count > 0 ? ` (${result.not_found_count} no encontrados)` : ''}`)
        setSelectedOrderIds(new Set())
        await loadOrders()
      } else {
        alert(`❌ Error: ${result.error || 'No se pudo eliminar'}`)
      }
    } catch (err) {
      console.error('Error en bulk delete:', err)
      alert(`❌ Error al eliminar: ${err.message}`)
    } finally {
      setDeletingBulk(false)
    }
  }

  const loadOrders = async () => {
    try {
      let url = `${API_URL}/orders?tipo=${activeTab}`
      if (filterEstado) url += `&estado=${filterEstado}`
      if (filterFechaDesde) url += `&fechaDesde=${filterFechaDesde}`
      if (filterFechaHasta) url += `&fechaHasta=${filterFechaHasta}`

      const response = await authFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        setOrders(result.data || [])
        setCurrentPage(1)
      }
    } catch (error) {
      console.error('Error cargando pedidos:', error)
    }
  }

  const handleLoadDebugRaw = async () => {
    setDebugLoading(true);
    setDebugRawData(null);
    try {
      const response = await authFetch(`${API_URL}/pedidos-ab/debug-raw?tipo=${activeTab}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setDebugRawData(result);
        setShowDebugPanel(true);
      } else {
        alert('Error: ' + (result.error || 'No se pudo cargar debug'));
      }
    } catch (err) {
      console.error('Error debug:', err);
      alert('Error al cargar debug raw');
    } finally {
      setDebugLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setCsvFile(file)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          console.log('CSV parseado:', results.data)
          setParsedCSV(results.data)
        } else {
          alert('CSV vacío o inválido')
        }
      },
      error: (error) => {
        console.error('Error parseando CSV:', error)
        alert('Error al leer el archivo CSV')
      }
    })
  }

  const handleGeneratePreview = async () => {
    if (!parsedCSV || parsedCSV.length === 0) {
      alert('No hay datos para procesar')
      return
    }

    setUploadLoading(true)

    try {
      const pedidos = {}

      parsedCSV.forEach(row => {
        const codigo = row.codigo_pedido
        if (!codigo) {
          console.warn('Fila sin código de pedido:', row)
          return
        }

        if (!pedidos[codigo]) {
          pedidos[codigo] = {
            codigo_pedido: codigo,
            tipo: (row.tipo || activeTab).toLowerCase(),
            cliente_proveedor: row.cliente_proveedor || '',
            contacto: row.contacto || '',
            telefono: row.telefono || '',
            lineas: []
          }
        }

        const linea = {
          producto_codigo: row.producto_codigo || '',
          cantidad_max: parseInt(row.cantidad_max || 0),
          cantidad_med: parseInt(row.cantidad_med || 0),
          cantidad_peq: parseInt(row.cantidad_peq || 0),
          precio_unitario: parseFloat(row.precio_unitario || 0),
          descuento: parseFloat(row.descuento || 0),
          iva_porcentaje: parseFloat(row.iva || 21)
        }

        if (isEntradaTipo(pedidos[codigo].tipo)) {
          linea.lote_nuevo = row.lote_nuevo || ''
          linea.fecha_vencimiento_nuevo = row.fecha_vencimiento_nuevo || ''
        }

        pedidos[codigo].lineas.push(linea)
      })

      const previews = []

      for (const pedido of Object.values(pedidos)) {
        console.log('Enviando pedido para preview:', pedido)
        
        const response = await authFetch(`${API_URL}/orders/preview`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(pedido)
        })
        if (!response) return;

        const result = await response.json()

        if (result.success) {
          previews.push({
            ...pedido,
            preview: result.data
          })
        } else {
          console.error('Error en preview:', result)
          alert(`Error en preview de ${pedido.codigo_pedido}: ${result.error}`)
        }
      }

      if (previews.length > 0) {
        setPreviewData(previews)
        setCurrentView('preview')
        setShowUploadModal(false)
      } else {
        alert('No se generaron previews válidas')
      }
      
      setUploadLoading(false)
    } catch (error) {
      console.error('Error generando preview:', error)
      alert('Error al generar vista previa: ' + error.message)
      setUploadLoading(false)
    }
  }

  const handleConfirmAll = async () => {
    if (!previewData || previewData.length === 0) return

    const confirmacion = window.confirm(
      `¿Confirmar ${previewData.length} pedido(s)?`
    )

    if (!confirmacion) return

    setUploadLoading(true)

    try {
      const resultados = []
      
      for (const pedido of previewData) {
        if (pedido.preview.resumen.errores > 0) {
          resultados.push({
            codigo: pedido.codigo_pedido,
            error: 'Pedido con errores en preview'
          })
          continue
        }

        const lineasConUbicacion = pedido.preview.lineas.map(l => ({
          producto_codigo: l.producto_codigo,
          cantidad_max: l.cantidad_max,
          cantidad_med: l.cantidad_med,
          cantidad_peq: l.cantidad_peq,
          precio_unitario: l.precio_unitario,
          descuento: l.descuento,
          iva_porcentaje: l.iva_porcentaje,
          lote_nuevo: l.lote_nuevo,
          fecha_vencimiento_nuevo: l.fecha_vencimiento_nuevo,
          ubicacion_asignada: l.ubicacion_asignada
        }))

        const createResponse = await authFetch(`${API_URL}/orders`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...pedido,
            lineas: lineasConUbicacion
          })
        })
        if (!createResponse) return;

        const createResult = await createResponse.json()

        if (createResult.success) {
          if (isEntradaTipo(pedido.tipo)) {
            await authFetch(`${API_URL}/orders/${createResult.data.id}/confirm`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
          }
          resultados.push({
            codigo: pedido.codigo_pedido,
            success: true
          })
        } else {
          resultados.push({
            codigo: pedido.codigo_pedido,
            error: createResult.error
          })
        }
      }

      const exitosos = resultados.filter(r => r.success).length
      const fallidos = resultados.filter(r => r.error).length

      alert(`✅ ${exitosos} pedidos procesados correctamente${fallidos > 0 ? `\n❌ ${fallidos} pedidos fallaron` : ''}`)
      
      setPreviewData(null)
      setParsedCSV(null)
      setCsvFile(null)
      setCurrentView('list')
      loadOrders()
      setUploadLoading(false)
    } catch (error) {
      console.error('Error confirmando pedidos:', error)
      alert('Error al procesar pedidos: ' + error.message)
      setUploadLoading(false)
    }
  }

  const handleMostrarVistaPrevia = async (linea) => {
    setLoadingLine(linea.id)
    setSelectedLineForPreview(linea)
    setModoReubicacion("automatico")
    setReubicacionesManuales({})

    try {
      const response = await authFetch(`${API_URL}/orders/lines/${linea.id}/preview-picking`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        setPreviewPicking(result.data)
        setShowPreviewModal(true)
      } else {
        alert('Error: ' + result.error)
      }
    } catch (error) {
      console.error('Error generando vista previa:', error)
      alert('Error al generar vista previa')
    } finally {
      setLoadingLine(null)
    }
  }

  const handleCambiarCantidadManual = (lote, campo, valor) => {
    setReubicacionesManuales(prev => ({
      ...prev,
      [lote]: {
        ...prev[lote],
        [campo]: parseInt(valor) || 0
      }
    }))
  }

  const handleCambiarNivelManual = (lote, nivelId) => {
    setReubicacionesManuales(prev => ({
      ...prev,
      [lote]: {
        ...prev[lote],
        nivel_id: nivelId
      }
    }))
  }

  const handleConfirmarPreparacion = async () => {
    if (!selectedLineForPreview) return

    setLoadingLine(selectedLineForPreview.id)
    setShowPreviewModal(false)

    try {
      const body = modoReubicacion === 'manual' && Object.keys(reubicacionesManuales).length > 0
        ? { reubicaciones_manuales: reubicacionesManuales }
        : {}

      const response = await authFetch(`${API_URL}/orders/lines/${selectedLineForPreview.id}/prepare`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      })
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        alert(`✅ ${result.message}`)
        handleViewOrder(selectedOrder.id)
        setPreviewPicking(null)
        setSelectedLineForPreview(null)
        setReubicacionesManuales({})
      } else {
        alert('Error: ' + result.error)
      }
    } catch (error) {
      console.error('Error preparando línea:', error)
      alert('Error al preparar línea')
    } finally {
      setLoadingLine(null)
    }
  }

  const handlePrepararLinea = async (linea) => {
    handleMostrarVistaPrevia(linea)
  }

  // Abrir modal de devolución multi-step
  const openRetornoModal = (linea) => {
    const lr = linea.line_return || {}
    setRetornoModalData({
      lineaId: linea.id,
      returnId: lr.id,
      productCode: linea.producto_codigo,
      productDesc: linea.producto_descripcion,
      lote: lr.lote_destino || '',
      fechaVencimiento: lr.fecha_vencimiento_final || '',
      estadoProducto: lr.estado_producto || '',
      ubicacion: lr.ubicacion_destino || '',
      fefoRecommendation: null,
      cantidades: {
        cajas: lr.cantidad_max_retornada ?? linea.cantidad_max ?? 0,
        displays: lr.cantidad_med_retornada ?? linea.cantidad_med ?? 0,
        unidades: lr.cantidad_peq_retornada ?? linea.cantidad_peq ?? 0
      }
    })
    setRetornoModalStep(1)
    setShowRetornoModal(true)
  }

  // Navegación entre pasos del modal de retorno
  const handleRetornoStepNext = async () => {
    if (retornoModalStep === 1) {
      if (!retornoModalData.lote.trim()) return alert('Ingresá el número de lote')
      if (!retornoModalData.fechaVencimiento) return alert('Ingresá la fecha de vencimiento')
      setRetornoModalStep(2)
    } else if (retornoModalStep === 2) {
      if (!retornoModalData.estadoProducto) return alert('Seleccioná el estado del producto')
      if (retornoModalData.estadoProducto === 'bueno') {
        await fetchFefoRecommendation()
        setRetornoModalStep(3)
      } else {
        setRetornoModalStep(4)
      }
    } else if (retornoModalStep === 3) {
      if (!retornoModalData.ubicacion.trim()) return alert('Ingresá la ubicación destino')
      setRetornoModalStep(4)
    }
  }

  const handleRetornoStepBack = () => {
    if (retornoModalStep === 4) {
      setRetornoModalStep(retornoModalData.estadoProducto === 'bueno' ? 3 : 2)
    } else if (retornoModalStep > 1) {
      setRetornoModalStep(retornoModalStep - 1)
    }
  }

  // Obtener recomendación FEFO para ubicación
  const fetchFefoRecommendation = async () => {
    try {
      const response = await authFetch(
        `${API_URL}/orders/products/${retornoModalData.productCode}/recommended-location?fecha_vencimiento=${retornoModalData.fechaVencimiento}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (response) {
        const result = await response.json()
        if (result.success && result.data?.ubicacion) {
          setRetornoModalData(prev => ({
            ...prev,
            ubicacion: result.data.ubicacion,
            fefoRecommendation: result.data
          }))
        }
      }
    } catch (err) {
      console.error('Error fetching FEFO:', err)
    }
  }

  // Enviar devolución procesada
  const handleRetornoSubmit = async () => {
    setRetornoModalLoading(true)
    try {
      // Iniciar retorno si no existe aún
      if (!retornoModalData.returnId) {
        const initResponse = await authFetch(
          `${API_URL}/orders/lines/${retornoModalData.lineaId}/iniciar-devolucion`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
          }
        )
        if (!initResponse) throw new Error('No se pudo iniciar el retorno')
        const initResult = await initResponse.json()
        if (!initResult.success) throw new Error(initResult.error || 'Error iniciando retorno')
        retornoModalData.returnId = initResult.data.id
      }

      // Procesar devolución
      const response = await authFetch(
        `${API_URL}/orders/returns/${retornoModalData.returnId}/process-devolucion-web`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            lote_destino: retornoModalData.lote,
            fecha_vencimiento_final: retornoModalData.fechaVencimiento,
            estado_producto: retornoModalData.estadoProducto,
            ubicacion_destino: retornoModalData.estadoProducto === 'bueno' ? retornoModalData.ubicacion : null
          })
        }
      )

      if (!response) throw new Error('Error de conexión')
      const result = await response.json()

      if (result.success) {
        setShowRetornoModal(false)
        await handleViewOrder(selectedOrder.id)
      } else {
        alert('Error: ' + (result.error || 'No se pudo procesar'))
      }
    } catch (error) {
      console.error('Error procesando retorno:', error)
      alert('Error: ' + error.message)
    } finally {
      setRetornoModalLoading(false)
    }
  }

  // 🆕 FUNCIÓN CORREGIDA: Detectar tipo y abrir modal correspondiente
  const handleCompletarLinea = async (linea) => {
    if (isEntradaTipo(selectedOrder.tipo)) {
      setSelectedLineForComplete(linea)
      setEntradaLotes([
        { id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }
      ])
      setEntradaSubmitProgress(null)
      setShowCompleteEntradaModal(true)
    } else if (isSalidaTipo(selectedOrder.tipo)) {
      // SALIDA: Primero verificar FEFO y luego abrir modal
      await handleVerifyAndShowSalidaModal(linea)
    }
  }

  // ✅ NUEVA FUNCIÓN: Verificar FEFO y mostrar modal de salida
  const handleVerifyAndShowSalidaModal = async (linea) => {
    setLoadingLine(linea.id)
    
    try {
      // Llamar a verify-and-update-fefo para obtener lotes actualizados
      const response = await authFetch(
        `${API_URL}/orders/lines/${linea.id}/verify-and-update-fefo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      )
      if (!response) return;

      const result = await response.json()
      
      if (!result.success) {
        alert('Error: ' + result.error)
        setLoadingLine(null)
        return
      }

      // Guardar los lotes FEFO obtenidos
      setLotesFefoParaSalida(result.data.lotes || [])
      
      // Actualizar la línea seleccionada con los lotes asignados
      setSelectedLineForComplete({
        ...linea,
        lotes_asignados: result.data.lotes
      })
      
      // Abrir modal de salida
      setShowCompleteSalidaModal(true)
      setLoadingLine(null)

    } catch (error) {
      console.error('Error verificando FEFO:', error)
      alert('Error al verificar lotes FEFO')
      setLoadingLine(null)
    }
  }

  const handleAddLote = () => {
    setEntradaLotes(prev => [...prev, {
      id: Date.now(),
      lote: '', fecha_vencimiento: '', ubicacion_manual: '',
      usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '',
      ubicacionRecomendada: null
    }]);
  };

  const handleRemoveLote = (loteId) => {
    setEntradaLotes(prev => prev.filter(l => l.id !== loteId));
  };

  const handleLoteChange = (loteId, field, value) => {
    setEntradaLotes(prev => prev.map(l => l.id === loteId ? { ...l, [field]: value } : l));
  };

  const getProductUnitLevels = () => {
    if (!selectedLineForComplete) return [{ key: 'unidades', label: 'Unidades', field: 'unidades' }];

    const convMed = selectedLineForComplete.cantidad_display_por_unidad_mayor || 1;
    const convPeq = selectedLineForComplete.cantidad_unidad_base_por_display || 1;

    const levels = [];

    if (convMed > 1) {
      levels.push({
        key: 'cajas',
        label: selectedLineForComplete.unidad_mayor || 'Cajas',
        field: 'cajas'
      });
    }

    if (convPeq > 1) {
      levels.push({
        key: 'displays',
        label: selectedLineForComplete.unidad_display || 'Displays',
        field: 'displays'
      });
    }

    levels.push({
      key: 'unidades',
      label: selectedLineForComplete.unidad_base || 'Unidades',
      field: 'unidades'
    });

    return levels;
  };

  const calcularUnidadesBase = (lote) => {
    if (!selectedLineForComplete) return 0;
    const convMed = selectedLineForComplete.cantidad_display_por_unidad_mayor || 1;
    const convPeq = selectedLineForComplete.cantidad_unidad_base_por_display || 1;
    const cajas = parseInt(lote.cajas) || 0;
    const displays = parseInt(lote.displays) || 0;
    const unidades = parseInt(lote.unidades) || 0;
    return (cajas * convMed * convPeq) + (displays * convPeq) + unidades;
  };

  const handleCalcUbicacionLote = async (lote) => {
    if (!selectedLineForComplete || !lote.lote || !lote.fecha_vencimiento) {
      alert('Debes especificar lote y fecha de vencimiento primero')
      return
    }

    try {
      const response = await authFetch(
        `${API_URL}/orders/products/${selectedLineForComplete.producto_codigo}/recommended-location?lote=${lote.lote}&fecha_vencimiento=${lote.fecha_vencimiento}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        setEntradaLotes(prev => prev.map(l => l.id === lote.id ? { ...l, ubicacionRecomendada: result.data.ubicacion_recomendada } : l));
      } else {
        alert('Error obteniendo ubicación recomendada')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Error al obtener ubicación recomendada')
    }
  };

  const handleConfirmCompleteEntrada = async () => {
    for (let i = 0; i < entradaLotes.length; i++) {
      const lote = entradaLotes[i];
      if (!lote.lote || !lote.fecha_vencimiento) {
        alert(`Lote ${i + 1}: Código de lote y fecha de vencimiento son obligatorios`)
        return
      }
      if (!lote.usar_ubicacion_recomendada && !lote.ubicacion_manual) {
        alert(`Lote ${i + 1}: Debes especificar una ubicación manual o usar la recomendada`)
        return
      }
      const cajas = parseInt(lote.cajas) || 0
      const displays = parseInt(lote.displays) || 0
      const unidades = parseInt(lote.unidades) || 0
      if (cajas === 0 && displays === 0 && unidades === 0) {
        alert(`Lote ${i + 1}: Debes ingresar al menos una cantidad`)
        return
      }
    }

    const loteCodes = entradaLotes.map(l => l.lote.trim().toUpperCase())
    const duplicates = loteCodes.filter((code, idx) => loteCodes.indexOf(code) !== idx)
    if (duplicates.length > 0) {
      alert(`Hay códigos de lote duplicados: ${[...new Set(duplicates)].join(', ')}`)
      return
    }

    const totalUnidadesBase = entradaLotes.reduce((sum, l) => sum + calcularUnidadesBase(l), 0);
    const restante = selectedLineForComplete.cantidad_solicitada - (selectedLineForComplete.cantidad_procesada || 0);
    if (totalUnidadesBase > restante) {
      alert(`No se pudo insertar las cantidades. Superan el límite: ${totalUnidadesBase} uds > ${restante} uds restantes`);
      return;
    }

    setLoadingComplete(true)
    setEntradaSubmitProgress({ current: 0, total: entradaLotes.length, loteActual: '' })

    const results = []
    for (let i = 0; i < entradaLotes.length; i++) {
      const lote = entradaLotes[i]
      setEntradaSubmitProgress({ current: i + 1, total: entradaLotes.length, loteActual: lote.lote })

      try {
        const body = {
          lote: lote.lote,
          fecha_vencimiento: lote.fecha_vencimiento,
          cantidad_max: parseInt(lote.cajas) || 0,
          cantidad_med: parseInt(lote.displays) || 0,
          cantidad_peq: parseInt(lote.unidades) || 0,
        }

        if (lote.usar_ubicacion_recomendada && lote.ubicacionRecomendada) {
          // Backend uses FEFO
        } else if (lote.ubicacion_manual) {
          body.ubicacion_manual = lote.ubicacion_manual
        }

        const response = await authFetch(
          `${API_URL}/orders/lines/${selectedLineForComplete.id}/complete-web-entrada`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body)
          }
        )
        if (!response) {
          results.push({ lote: lote.lote, success: false, error: 'Error de conexión' })
          break
        }

        const data = await response.json()
        results.push({ lote: lote.lote, success: data.success, data })

        if (!data.success) {
          alert(`Error en lote ${lote.lote}: ${data.error || 'Error desconocido'}`)
          break
        }
      } catch (err) {
        results.push({ lote: lote.lote, success: false, error: err.message })
        alert(`Error en lote ${lote.lote}: ${err.message}`)
        break
      }
    }

    const allSuccess = results.every(r => r.success)
    if (allSuccess && results.length > 0) {
      const lastData = results[results.length - 1].data?.data
      if (entradaLotes.length === 1 && lastData) {
        if (lastData.linea_completa) {
          alert(`Linea completada!\nUbicacion: ${lastData.ubicacion_asignada}\nTotal recibido: ${lastData.cantidad_procesada}/${lastData.cantidad_solicitada} uds`)
        } else {
          alert(`Recepcion parcial registrada.\nUbicacion: ${lastData.ubicacion_asignada}\nRecibido: ${lastData.cantidad_procesada}/${lastData.cantidad_solicitada} uds\nFaltan: ${lastData.cantidad_solicitada - lastData.cantidad_procesada} uds`)
        }
      } else {
        alert(`${results.length} lote(s) procesados correctamente`)
      }
    }

    setLoadingComplete(false)
    setEntradaSubmitProgress(null)
    setShowCompleteEntradaModal(false)
    setEntradaLotes([{ id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }])
    handleViewOrder(selectedOrder.id)
    loadOrders()
  }

  // ✅ FUNCIÓN CORREGIDA: Confirmar completar salida (usa ruta correcta)
  const handleConfirmCompleteSalida = async () => {
    setLoadingComplete(true)

    try {
      const response = await authFetch(
        `${API_URL}/orders/lines/${selectedLineForComplete.id}/complete-web-salida`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      )
      if (!response) return;

      const result = await response.json()

      if (result.success) {
        alert(`✅ ${result.message}`)
        setShowCompleteSalidaModal(false)
        setLotesFefoParaSalida([])
        handleViewOrder(selectedOrder.id)
        loadOrders()
      } else {
        alert('Error: ' + result.error)
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Error al completar salida')
    } finally {
      setLoadingComplete(false)
    }
  }

  const handleEnviarPedido = async (orderId) => {
    const confirmacion = window.confirm(
      '¿Enviar pedido? Se restará el stock del almacén de forma definitiva.'
    )
    if (!confirmacion) return

    try {
      const response = await authFetch(`${API_URL}/orders/${orderId}/ship`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        alert('✅ Pedido enviado. Stock reducido del almacén.')
        handleViewOrder(orderId)
        loadOrders()
      } else {
        alert('Error: ' + result.error)
      }
    } catch (error) {
      console.error('Error enviando pedido:', error)
      alert('Error al enviar pedido')
    }
  }

  const handleOpenReturnModal = (linea) => {
    setSelectedLineForReturn(linea);
    setReturnData({
      cantidad_max_retornada: '',
      cantidad_med_retornada: '',
      cantidad_peq_retornada: '',
      motivo: '',
      estado_producto: 'bueno'
    });
    setShowReturnModal(true);
  };

  const handleCreateReturn = async () => {
    const cajas = parseInt(returnData.cantidad_max_retornada) || 0;
    const displays = parseInt(returnData.cantidad_med_retornada) || 0;
    const unidades = parseInt(returnData.cantidad_peq_retornada) || 0;

    if (cajas === 0 && displays === 0 && unidades === 0) {
      alert('Por favor, especifica al menos una cantidad a retornar');
      return;
    }

    if (!returnData.motivo) {
      alert('Por favor, especifica el motivo del retorno');
      return;
    }

    if (cajas > selectedLineForReturn.cantidad_max || 
        displays > selectedLineForReturn.cantidad_med || 
        unidades > selectedLineForReturn.cantidad_peq) {
      alert('Las cantidades a retornar no pueden ser mayores a las cantidades enviadas');
      return;
    }

    setLoadingReturn(true);
    try {
      const response = await authFetch(
        `${API_URL}/orders/lines/${selectedLineForReturn.id}/create-return`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            cantidad_max_retornada: cajas,
            cantidad_med_retornada: displays,
            cantidad_peq_retornada: unidades,
            motivo: returnData.motivo,
            estado_producto: returnData.estado_producto
          })
        }
      );
      if (!response) return;
      const result = await response.json();

      if (result.success) {
        alert('✅ Retorno creado exitosamente en estado pendiente. Debe ser procesado desde la app móvil.');
        setShowReturnModal(false);
        setSelectedLineForReturn(null);
        handleViewOrder(selectedOrder.id);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error creando retorno:', error);
      alert('Error al crear retorno: ' + error.message);
    } finally {
      setLoadingReturn(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await authFetch(`${API_URL}/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response) return;
      const result = await response.json()

      if (result.success) {
        setSelectedOrder(result.data)
        setCurrentView('detail')
      }
    } catch (error) {
      console.error('Error obteniendo detalle:', error)
    }
  }

  const handleExportOrder = async (formato) => {
    if (!selectedOrder) return

    setExportLoading(true)
    try {
      const response = await authFetch(`${API_URL}/orders/${selectedOrder.id}/export/${formato}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response) return;
      if (!response.ok) {
        throw new Error('Error al exportar')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      const extension = formato === 'excel' ? 'xlsx' : formato
      a.download = `${selectedOrder.codigo_pedido}.${extension}`
      
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      setShowExportModal(false)
    } catch (error) {
      console.error('Error exportando:', error)
      alert('Error al exportar el pedido')
    } finally {
      setExportLoading(false)
    }
  }

  // Descargar CSV con TODAS las líneas de TODOS los pedidos del tipo activo.
  // Respeta los filtros de estado y fechas que estén aplicados en la vista.
  const handleExportTipoCsv = async () => {
    setExportTipoLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterEstado) params.set('estado', filterEstado)
      if (filterFechaDesde) params.set('fechaDesde', filterFechaDesde)
      if (filterFechaHasta) params.set('fechaHasta', filterFechaHasta)
      const qs = params.toString() ? `?${params.toString()}` : ''

      const response = await authFetch(`${API_URL}/orders/export/csv/${activeTab}${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response) return
      if (!response.ok) {
        throw new Error('Error al exportar el CSV')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pedidos_${activeTab}_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error exportando CSV por tipo:', error)
      alert('Error al exportar el CSV')
    } finally {
      setExportTipoLoading(false)
    }
  }

  const getFilteredOrders = () => {
    return orders.filter(order => {
      const matchSearch = searchTerm === '' ||
        order.codigo_pedido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.cliente_proveedor?.toLowerCase().includes(searchTerm.toLowerCase())

      return matchSearch
    })
  }

  const getPaginatedOrders = () => {
    const filtered = getFilteredOrders()
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    const paginated = filtered.slice(start, start + ITEMS_PER_PAGE)
    return { paginated, totalPages, totalItems: filtered.length }
  }

  const getEstadoBadgeClass = (estado) => {
    const clases = {
      'pendiente': 'badge-pendiente',
      'en_preparacion': 'badge-preparacion',
      'completado': 'badge-completado',
      'enviado': 'badge-enviado',
      'retornado_completo': 'badge-retornado-completo',
      'retornado_parcial': 'badge-retornado-parcial',
      'procesado': 'badge-procesado',
      'bueno': 'badge-bueno',
      'dañado': 'badge-dañado',
      'caducado': 'badge-caducado'
    }
    return clases[estado] || 'badge-default'
  }

  const getEstadoIcon = (estado) => {
    const iconos = {
      'pendiente': <AlertCircle size={14} />,
      'en_preparacion': <PlayCircle size={14} />,
      'completado': <CheckCircle size={14} />,
      'enviado': <Truck size={14} />,
      'retornado_completo': <RotateCcw size={14} />,
      'retornado_parcial': <RotateCcw size={14} />,
      'procesado': <CheckCircle size={14} />
    }
    return iconos[estado] || <AlertCircle size={14} />
  }

  // ============================================
  // HELPERS DE TIPO
  // ============================================
  const TIPOS_ENTRADA = ['entrada', 'compra_mercaderia', 'devolucion_mercaderia', 'devolucion_salida']
  const TIPOS_SALIDA = ['salida', 'venta_mercaderia', 'devolucion_compra', 'picking_salida']
  const TIPOS_DEVOLUCION = ['devolucion_compra', 'devolucion_mercaderia', 'devolucion_salida']
  const TIPOS_PLACA = ['picking_salida', 'devolucion_salida']
  const esTipoPlaca = (tipo) => TIPOS_PLACA.includes(tipo)
  const isEntradaTipo = (tipo) => TIPOS_ENTRADA.includes(tipo)
  const isSalidaTipo = (tipo) => TIPOS_SALIDA.includes(tipo)
  const isDevolucionTipo = (tipo) => TIPOS_DEVOLUCION.includes(tipo)
  const isCompraMercaderia = () => selectedOrder?.tipo === 'compra_mercaderia'

  // ============================================
  // AUTO-SYNC AB
  // ============================================
  useEffect(() => {
    const checkSync = async () => {
      if (esTipoPlaca(activeTab)) {
        setSyncStatus({ loading: false, nuevos: 0, data: null })
        return
      }
      try {
        setSyncStatus(prev => ({ ...prev, loading: true }))
        const response = await authFetch(`${API_URL}/pedidos-ab/sync-check`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response) return
        const data = await response.json()
        if (data.success) {
          setSyncStatus({ loading: false, nuevos: data.data.resumen.nuevos, data: data.data })
        } else {
          setSyncStatus({ loading: false, nuevos: 0, data: null })
        }
      } catch (err) {
        console.error('Error en sync-check:', err)
        setSyncStatus({ loading: false, nuevos: 0, data: null })
      }
    }
    checkSync()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const handleSyncPicking = async () => {
    const esDev = activeTab === 'devolucion_salida'
    const confirmar = window.confirm(
      esDev ? '¿Sincronizar devoluciones por placa desde AB?' : '¿Sincronizar pedidos de picking desde AB?'
    )
    if (!confirmar) return

    try {
      setSyncProgress({ phase: 'starting', current: 0, total: 0, exitosos: 0, errores: 0, message: 'Iniciando...' })
      setSyncStatus(prev => ({ ...prev, loading: true }))

      const endpoint = esDev
        ? `${API_URL}/pedidos-ab/sincronizar-devoluciones-placa`
        : `${API_URL}/pedidos-ab/sincronizar-picking`
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (!line.trim()) continue
          try {
            const data = JSON.parse(line)
            setSyncProgress(data)

            if (data.phase === 'done') {
              setSyncStatus({ loading: false, nuevos: 0, data: null })
              loadOrders()
            }
          } catch (e) {}
        }
      }

      if (buffer.trim()) {
        try {
          const data = JSON.parse(buffer)
          setSyncProgress(data)
          if (data.phase === 'done') {
            setSyncStatus({ loading: false, nuevos: 0, data: null })
            loadOrders()
          }
        } catch (e) {}
      }

      setTimeout(() => setSyncProgress(null), 30000)

    } catch (err) {
      console.error('Error sincronizando picking:', err)
      setSyncProgress({ phase: 'error', message: 'Error de conexión: ' + err.message })
      setSyncStatus(prev => ({ ...prev, loading: false }))
      setTimeout(() => setSyncProgress(null), 5000)
    }
  }

  const handleSyncAll = async () => {
    if (!syncStatus.data?.pedidos_nuevos?.length) return
    const confirmar = window.confirm(`¿Sincronizar ${syncStatus.data.pedidos_nuevos.length} pedido(s) de AB?`)
    if (!confirmar) return

    try {
      setSyncProgress({ phase: 'starting', current: 0, total: 0, exitosos: 0, errores: 0, message: 'Iniciando...' })
      setSyncStatus(prev => ({ ...prev, loading: true }))

      const syncEndpoint = activeTab === 'picking_salida'
        ? `${API_URL}/pedidos-ab/sincronizar-picking`
        : activeTab === 'devolucion_salida'
          ? `${API_URL}/pedidos-ab/sincronizar-devoluciones-placa`
          : `${API_URL}/pedidos-ab/sincronizar-stream`
      const response = await fetch(syncEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // keep incomplete line

        for (const line of lines) {
          if (!line.trim()) continue
          try {
            const data = JSON.parse(line)
            setSyncProgress(data)

            if (data.phase === 'done') {
              setSyncStatus({ loading: false, nuevos: 0, data: null })
              loadOrders()
            }
          } catch (e) {
            // skip malformed lines
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const data = JSON.parse(buffer)
          setSyncProgress(data)
          if (data.phase === 'done') {
            setSyncStatus({ loading: false, nuevos: 0, data: null })
            loadOrders()
          }
        } catch (e) {}
      }

      // Clear progress after 30 seconds (more time to review errors)
      setTimeout(() => setSyncProgress(null), 30000)

    } catch (err) {
      console.error('Error sincronizando:', err)
      setSyncProgress({ phase: 'error', message: 'Error de conexión: ' + err.message })
      setSyncStatus(prev => ({ ...prev, loading: false }))
      setTimeout(() => setSyncProgress(null), 5000)
    }
  }


  return (
    <div className="order-management">
      {/* HEADER */}
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <Package className="title-icon" />
            <h1>Gestión de Pedidos</h1>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isAdmin && selectedOrderIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deletingBulk}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deletingBulk ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: deletingBulk ? 0.6 : 1,
                }}
              >
                {deletingBulk ? (
                  <RefreshCw size={14} className="spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Eliminar seleccionados ({selectedOrderIds.size})
              </button>
            )}
            <button className="btn-upload" onClick={() => setShowUploadModal(true)}>
              <Upload size={16} />
              Cargar CSV
            </button>
          </div>
        </div>

        {/* TABS */}
        {vistaPedidos === 'compra_venta' && (
          <div className="tabs">
            {[
              { key: 'compra_mercaderia', label: 'Compra Mercadería' },
              { key: 'venta_mercaderia', label: 'Venta Mercadería' },
              { key: 'devolucion_mercaderia', label: 'Devolución Mercadería' },
              { key: 'devolucion_compra', label: 'Devolución Compra' },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(key)
                  setCurrentView('list')
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {vistaPedidos === 'placa' && (
          <div className="tabs">
            {[
              { key: 'picking_salida', label: 'Pedidos con Placa' },
              { key: 'devolucion_salida', label: 'Devoluciones con Placa' },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(key)
                  setCurrentView('list')
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* VISTA LISTA */}
      {currentView === 'list' && (
        <div className="list-container">
          {/* FILTROS */}
          <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar por código, cliente..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_preparacion">En Preparación</option>
              <option value="completado">Completado</option>
              <option value="enviado">Enviado</option>
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={14} style={{ color: '#6B7280' }} />
              <input
                type="date"
                value={filterFechaDesde}
                onChange={(e) => setFilterFechaDesde(e.target.value)}
                className="filter-select"
                style={{ minWidth: '130px' }}
                title="Fecha desde"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#9CA3AF', fontSize: '12px' }}>a</span>
              <input
                type="date"
                value={filterFechaHasta}
                onChange={(e) => setFilterFechaHasta(e.target.value)}
                className="filter-select"
                style={{ minWidth: '130px' }}
                title="Fecha hasta"
              />
            </div>
            {(filterFechaDesde || filterFechaHasta) && (
              <button
                className="btn-link"
                onClick={() => { setFilterFechaDesde(''); setFilterFechaHasta(''); }}
                style={{ fontSize: '12px', color: '#EF4444' }}
              >
                <X size={12} /> Limpiar fechas
              </button>
            )}
            <button
              onClick={handleLoadDebugRaw}
              disabled={debugLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', backgroundColor: '#7c3aed', color: 'white',
                border: 'none', borderRadius: '6px',
                cursor: debugLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: '13px',
                opacity: debugLoading ? 0.7 : 1, marginLeft: 'auto'
              }}
              title="Ver respuesta cruda de la API AB para el tab actual"
            >
              {debugLoading ? <RefreshCw size={14} className="spin" /> : <Bug size={14} />}
              Debug Raw AB
            </button>
            <button
              onClick={handleExportTipoCsv}
              disabled={exportTipoLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', backgroundColor: '#16a34a', color: 'white',
                border: 'none', borderRadius: '6px',
                cursor: exportTipoLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: '13px',
                opacity: exportTipoLoading ? 0.7 : 1
              }}
              title="Descargar CSV con todas las líneas de todos los pedidos de este tipo (respeta los filtros de estado y fecha)"
            >
              {exportTipoLoading ? <RefreshCw size={14} className="spin" /> : <Download size={14} />}
              Descargar CSV
            </button>
          </div>

          {/* SYNC BANNER */}
          {syncProgress && syncProgress.phase !== 'done' && syncProgress.phase !== 'error' && (
            <div style={{ padding: '16px', backgroundColor: '#e3f2fd', borderRadius: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {syncProgress.phase === 'fetching' && 'Obteniendo datos de AB...'}
                  {syncProgress.phase === 'comparing' && (syncProgress.conteo_tipos
                    ? `${syncProgress.message}`
                    : 'Comparando con base de datos...'
                  )}
                  {syncProgress.phase === 'ready' && (
                    <>
                      {syncProgress.total} nuevos encontrados
                      {syncProgress.nuevos_por_tipo && ` (${Object.entries(syncProgress.nuevos_por_tipo).map(([k,v]) => `${k}: ${v}`).join(', ')})`}
                      {syncProgress.devolucion_mercaderia_ya_existen?.length > 0 && (
                        <span style={{ color: '#e65100', display: 'block', fontSize: '12px' }}>
                          Dev. mercadería ya existentes: {syncProgress.devolucion_mercaderia_ya_existen.join(', ')}
                        </span>
                      )}
                    </>
                  )}
                  {syncProgress.phase === 'progress' && `Procesando: ${syncProgress.current}/${syncProgress.total}`}
                  {syncProgress.phase === 'starting' && 'Iniciando sincronización...'}
                </span>
                {syncProgress.phase === 'progress' && (
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    {syncProgress.exitosos} creados, {syncProgress.errores} errores
                  </span>
                )}
              </div>
              {syncProgress.total > 0 && (
                <div style={{ width: '100%', backgroundColor: '#bbdefb', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.round((syncProgress.current / syncProgress.total) * 100)}%`,
                    backgroundColor: syncProgress.errores > 0 && syncProgress.exitosos === 0 ? '#ef5350' : '#1976d2',
                    height: '100%',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              )}
              {syncProgress.ultimo_error && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#c62828', backgroundColor: '#ffebee', padding: '6px 10px', borderRadius: '4px' }}>
                  Error: {syncProgress.ultimo_error}
                </div>
              )}
              {syncProgress.errores_detalle && syncProgress.errores_detalle.length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#c62828', backgroundColor: '#ffebee', padding: '8px 10px', borderRadius: '4px', maxHeight: '120px', overflowY: 'auto' }}>
                  {syncProgress.errores_detalle.map((e, i) => (
                    <div key={i} style={{ marginBottom: '2px' }}>
                      <strong>[{e.tipo}]</strong> {e.codigo}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {syncProgress && syncProgress.phase === 'done' && (
            <div style={{ padding: '12px 16px', backgroundColor: syncProgress.errores > 0 ? '#fff3e0' : '#e8f5e9', borderRadius: '8px', marginBottom: '16px' }}>
              <span style={{ fontWeight: 600 }}>
                Sincronización completada: {syncProgress.exitosos} creados, {syncProgress.errores} errores
              </span>
              {syncProgress.errores_detalle && syncProgress.errores_detalle.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#c62828', backgroundColor: '#ffebee', padding: '8px 10px', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                  {syncProgress.errores_detalle.map((e, i) => (
                    <div key={i} style={{ marginBottom: '2px' }}>
                      <strong>[{e.tipo}]</strong> {e.codigo}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {syncProgress && syncProgress.phase === 'error' && (
            <div style={{ padding: '12px 16px', backgroundColor: '#ffebee', borderRadius: '8px', marginBottom: '16px' }}>
              <span style={{ fontWeight: 600, color: '#c62828' }}>Error: {syncProgress.message}</span>
            </div>
          )}
          {!syncProgress && syncStatus.loading && (
            <div style={{ padding: '12px 16px', backgroundColor: '#e3f2fd', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw size={16} className="spin" />
              <span>Verificando pedidos en AB...</span>
            </div>
          )}
          {!syncProgress && syncStatus.nuevos > 0 && !syncStatus.loading && (
            <div style={{ padding: '12px 16px', backgroundColor: '#fff3e0', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>
                Se encontraron {syncStatus.nuevos} pedido(s) nuevo(s) en AB
              </span>
              <button
                onClick={handleSyncAll}
                style={{ padding: '6px 16px', backgroundColor: '#ff9800', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
              >
                Sincronizar todos
              </button>
            </div>
          )}
          {!syncProgress && esTipoPlaca(activeTab) && (
            <div style={{ padding: '12px 16px', backgroundColor: '#e3f2fd', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>
                {activeTab === 'devolucion_salida'
                  ? 'Sincronizar devoluciones por placa desde AB'
                  : 'Sincronizar pedidos con placa desde AB'}
              </span>
              <button
                onClick={handleSyncPicking}
                disabled={syncStatus.loading}
                style={{ padding: '6px 16px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
              >
                <RefreshCw size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                {activeTab === 'devolucion_salida' ? 'Sincronizar Devoluciones' : 'Sincronizar Placa'}
              </button>
            </div>
          )}


          {/* TABLA DE PEDIDOS */}
          <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    {isAdmin && (
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          onChange={handleToggleAllVisible}
                          checked={
                            getPaginatedOrders().paginated.length > 0 &&
                            getPaginatedOrders().paginated.every(o => selectedOrderIds.has(o.id))
                          }
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                    )}
                    <th style={{ width: '120px' }}>Código</th>
                    <th>{esTipoPlaca(activeTab) ? 'Vehículo' : 'Cliente/Proveedor'}</th>
                    {esTipoPlaca(activeTab) && <th style={{ width: '120px' }}>Placa</th>}
                    <th style={{ width: '100px' }}>Fecha</th>
                    <th style={{ width: '100px', textAlign: 'right' }}>Total</th>
                    <th style={{ width: '150px' }}>Estado</th>
                    <th style={{ width: isAdmin ? '110px' : '80px', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedOrders().totalItems === 0 ? (
                    <tr>
                      <td colSpan={(isAdmin ? 1 : 0) + (esTipoPlaca(activeTab) ? 7 : 6)} className="no-data">
                        <Package size={32} />
                        <p>No hay pedidos</p>
                      </td>
                    </tr>
                  ) : (
                    getPaginatedOrders().paginated.map(order => (
                      <tr key={order.id} className="clickable-row" onClick={() => handleViewOrder(order.id)}>
                        {isAdmin && (
                          <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.has(order.id)}
                              onChange={() => handleToggleOrderSelection(order.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                        )}
                        <td>
                          <div className="code-cell">
                            <Hash size={12} />
                            {order.codigo_pedido}
                          </div>
                        </td>
                        <td>{order.cliente_proveedor}</td>
                        {esTipoPlaca(activeTab) && <td>{order.placa || '—'}</td>}
                        <td>{order.fecha_pedido}</td>
                        <td style={{ textAlign: 'right' }}>
                          <strong>S/{order.total}</strong>
                        </td>
                        <td>
                          <span className={`badge ${getEstadoBadgeClass(order.estado)}`}>
                            {getEstadoIcon(order.estado)}
                            {order.estado.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              className="btn-icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewOrder(order.id)
                              }}
                              title="Ver detalle"
                            >
                              <Eye size={16} />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteOrder(order)
                                }}
                                disabled={deletingRowId === order.id}
                                title="Eliminar pedido"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '28px',
                                  height: '28px',
                                  background: 'transparent',
                                  border: '1px solid #fecaca',
                                  borderRadius: '6px',
                                  color: '#dc2626',
                                  cursor: deletingRowId === order.id ? 'not-allowed' : 'pointer',
                                  opacity: deletingRowId === order.id ? 0.5 : 1,
                                }}
                              >
                                {deletingRowId === order.id ? (
                                  <RefreshCw size={14} className="spin" />
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINACIÓN */}
            {getPaginatedOrders().totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: '#F9FAFB', borderRadius: '0 0 8px 8px',
                borderTop: '1px solid #E5E7EB', fontSize: '13px', color: '#6B7280',
              }}>
                <span>
                  Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, getPaginatedOrders().totalItems)} de {getPaginatedOrders().totalItems} pedidos
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px',
                      background: currentPage === 1 ? '#F3F4F6' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer',
                      fontSize: '12px', color: currentPage === 1 ? '#9CA3AF' : '#374151',
                    }}
                  >
                    &laquo;
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px',
                      background: currentPage === 1 ? '#F3F4F6' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer',
                      fontSize: '12px', color: currentPage === 1 ? '#9CA3AF' : '#374151',
                    }}
                  >
                    &lsaquo;
                  </button>
                  {Array.from({ length: Math.min(5, getPaginatedOrders().totalPages) }, (_, i) => {
                    const total = getPaginatedOrders().totalPages;
                    let start = Math.max(1, currentPage - 2);
                    if (start + 4 > total) start = Math.max(1, total - 4);
                    const page = start + i;
                    if (page > total) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: '4px 10px', border: '1px solid #D1D5DB', borderRadius: '4px',
                          background: currentPage === page ? '#2563EB' : '#fff',
                          color: currentPage === page ? '#fff' : '#374151',
                          cursor: 'pointer', fontSize: '12px', fontWeight: currentPage === page ? 600 : 400,
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(getPaginatedOrders().totalPages, p + 1))}
                    disabled={currentPage === getPaginatedOrders().totalPages}
                    style={{
                      padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px',
                      background: currentPage === getPaginatedOrders().totalPages ? '#F3F4F6' : '#fff',
                      cursor: currentPage === getPaginatedOrders().totalPages ? 'default' : 'pointer',
                      fontSize: '12px', color: currentPage === getPaginatedOrders().totalPages ? '#9CA3AF' : '#374151',
                    }}
                  >
                    &rsaquo;
                  </button>
                  <button
                    onClick={() => setCurrentPage(getPaginatedOrders().totalPages)}
                    disabled={currentPage === getPaginatedOrders().totalPages}
                    style={{
                      padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px',
                      background: currentPage === getPaginatedOrders().totalPages ? '#F3F4F6' : '#fff',
                      cursor: currentPage === getPaginatedOrders().totalPages ? 'default' : 'pointer',
                      fontSize: '12px', color: currentPage === getPaginatedOrders().totalPages ? '#9CA3AF' : '#374151',
                    }}
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
        </div>
      )}

      {/* VISTA PREVIEW */}
      {currentView === 'preview' && previewData && (
        <div className="preview-container">
          <div className="preview-header">
            <button className="btn-back" onClick={() => {
              setCurrentView('list')
              setPreviewData(null)
            }}>
              <ChevronLeft size={16} />
              Volver
            </button>
            <h2>Vista Previa ({previewData.length} pedidos)</h2>
            <button className="btn-confirm" onClick={handleConfirmAll}>
              <CheckCircle size={16} />
              Confirmar Todos
            </button>
          </div>

          {previewData.map((pedido, idx) => (
            <div key={idx} className="preview-card">
              <div className="preview-card-header">
                <div>
                  <h3>{pedido.codigo_pedido}</h3>
                  <p>{pedido.cliente_proveedor}</p>
                </div>
                <div className="preview-stats">
                  <span className="stat-ok">✓ {pedido.preview.resumen.ok}</span>
                  {pedido.preview.resumen.errores > 0 && (
                    <span className="stat-error">✗ {pedido.preview.resumen.errores}</span>
                  )}
                  {pedido.preview.resumen.avisos > 0 && (
                    <span className="stat-warning">⚠ {pedido.preview.resumen.avisos}</span>
                  )}
                </div>
              </div>

              <table className="data-table compact">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Img</th>
                    <th>Producto</th>
                    {isEntradaTipo(pedido.tipo) ? (
                      <th style={{ width: '100px' }}>Cantidad</th>
                    ) : (
                      <>
                        <th style={{ width: '60px' }}>C</th>
                        <th style={{ width: '60px' }}>D</th>
                        <th style={{ width: '60px' }}>U</th>
                      </>
                    )}
                    {isEntradaTipo(pedido.tipo) && <th style={{ width: '120px' }}>Lote</th>}
                    {isEntradaTipo(pedido.tipo) && <th style={{ width: '100px' }}>Vencimiento</th>}
                    {isEntradaTipo(pedido.tipo) && <th style={{ width: '120px' }}>Ubicación</th>}
                    {isSalidaTipo(pedido.tipo) && <th style={{ width: '100px' }}>Stock</th>}
                    <th style={{ width: '80px' }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {pedido.preview.lineas.map((linea, lineaIdx) => (
                    <tr key={lineaIdx} className={linea.estado}>
                      <td>
                        <div className="product-image-cell">
                          {linea.imagen_url ? (
                            <img src={linea.imagen_url} alt={linea.producto_descripcion} />
                          ) : (
                            <div className="no-image">
                              <ImageIcon size={16} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="product-info">
                          <strong>{linea.producto_codigo}</strong>
                          <span>{linea.producto_descripcion}</span>
                        </div>
                      </td>
                      {isEntradaTipo(pedido.tipo) ? (
                        <td><strong>{linea.cantidad_solicitada || 0}</strong> uds</td>
                      ) : (
                        <>
                          <td>{linea.cantidad_max}</td>
                          <td>{linea.cantidad_med}</td>
                          <td>{linea.cantidad_peq}</td>
                        </>
                      )}
                      {isEntradaTipo(pedido.tipo) && (
                        <>
                          <td>{linea.lote_nuevo}</td>
                          <td>{linea.fecha_vencimiento_nuevo}</td>
                          <td>
                            {linea.ubicacion_asignada ? (
                              <div className="ubicacion-cell">
                                <MapPin size={12} />
                                {linea.ubicacion_asignada.codigo_ubicacion}
                              </div>
                            ) : (
                              <span className="text-error">Sin ubicación</span>
                            )}
                          </td>
                        </>
                      )}
                      {isSalidaTipo(pedido.tipo) && (
                        <td>
                          {linea.stock_info && (
                            <div className="stock-mini">
                              <span className="stock-value">{linea.stock_info.disponible}</span>
                              <span className="stock-label">disponible</span>
                            </div>
                          )}
                        </td>
                      )}
                      <td>
                        {linea.estado === 'ok' ? (
                          <span className="badge badge-ok">OK</span>
                        ) : linea.estado === 'aviso' ? (
                          <span className="badge badge-warning">Aviso</span>
                        ) : (
                          <span className="badge badge-error">Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {pedido.preview.lineas.some(l => l.error || l.aviso) && (
                <div className="errors-box">
                  {pedido.preview.lineas.filter(l => l.error || l.aviso).map((linea, idx) => (
                    <div key={idx} className={linea.error ? "error-item" : "warning-item"}>
                      <AlertCircle size={14} />
                      <span>{linea.producto_codigo}: {linea.error || linea.aviso}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* VISTA DETALLE */}
      {currentView === 'detail' && selectedOrder && (
        <div className="detail-container">
          <div className="detail-header">
            <button className="btn-back" onClick={() => {
              setCurrentView('list')
              setSelectedOrder(null)
            }}>
              <ChevronLeft size={16} />
              Volver
            </button>
            <h2>Pedido: {selectedOrder.codigo_pedido}</h2>
            <div className="detail-actions">
              <button
                className="btn-export"
                onClick={() => setShowExportModal(true)}
                title="Exportar pedido"
              >
                <Download size={16} />
                Exportar
              </button>
              {isSalidaTipo(selectedOrder.tipo) && !isDevolucionTipo(selectedOrder.tipo) && selectedOrder.estado === 'completado' && (
                <button
                  className="btn-ship"
                  onClick={() => handleEnviarPedido(selectedOrder.id)}
                >
                  <Send size={16} />
                  Enviar Pedido
                </button>
              )}
            </div>
          </div>

          {/* INFO DEL PEDIDO */}
          <div className="info-section">
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">
                  <Package size={14} />
                  Tipo
                </div>
                <div className="info-value capitalize">{selectedOrder.tipo}</div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <User size={14} />
                  Cliente/Proveedor
                </div>
                <div className="info-value">{selectedOrder.cliente_proveedor}</div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <Calendar size={14} />
                  Fecha
                </div>
                <div className="info-value">{selectedOrder.fecha_pedido}</div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <AlertCircle size={14} />
                  Estado
                </div>
                <div className="info-value">
                  <span className={`badge ${getEstadoBadgeClass(selectedOrder.estado)}`}>
                    {getEstadoIcon(selectedOrder.estado)}
                    {selectedOrder.estado.replace('_', ' ')}
                  </span>
                </div>
              </div>
              {selectedOrder.placa && (
                <div className="info-item">
                  <div className="info-label">
                    <Truck size={14} />
                    Placa
                  </div>
                  <div className="info-value">{selectedOrder.placa}</div>
                </div>
              )}
              {selectedOrder.consolidado && (
                <div className="info-item">
                  <div className="info-label">
                    <Hash size={14} />
                    Consolidado
                  </div>
                  <div className="info-value">{selectedOrder.consolidado}</div>
                </div>
              )}
            </div>

            <div className="totals-grid">
              <div className="total-item">
                <span>Subtotal:</span>
                <strong>S/{selectedOrder.subtotal}</strong>
              </div>
              <div className="total-item">
                <span>IVA:</span>
                <strong>S/{selectedOrder.iva}</strong>
              </div>
              <div className="total-item highlight">
                <span>Total:</span>
                <strong>S/{selectedOrder.total}</strong>
              </div>
            </div>
          </div>

          {/* LÍNEAS DEL PEDIDO - TABLA */}
          <div className="lines-section">
            <h3>Líneas del Pedido ({selectedOrder.lineas?.length || 0})</h3>

            <div className="table-wrapper">
              <table className="data-table lines-table">
                <thead>
                  {(selectedOrder.es_devolucion || isDevolucionTipo(selectedOrder.tipo)) ? (
                  <tr>
                    <th>Producto</th>
                    <th style={{ width: '50px' }}>C</th>
                    <th style={{ width: '50px' }}>D</th>
                    <th style={{ width: '50px' }}>U</th>
                    <th style={{ width: '120px' }}>Estado Retorno</th>
                    <th style={{ width: '140px' }}>Estado Producto</th>
                    <th style={{ width: '130px' }}>Lote Destino</th>
                    <th style={{ width: '130px' }}>F. Vencimiento</th>
                    <th style={{ width: '130px' }}>Ubicación Destino</th>
                    <th style={{ width: '130px' }}>Procesado por</th>
                    <th style={{ width: '130px' }}>Acciones</th>
                  </tr>
                  ) : (
                  <tr>
                    <th style={{ width: '60px' }}>Img</th>
                    <th>Producto</th>
                    {isEntradaTipo(selectedOrder.tipo) ? (
                      <>
                        <th style={{ width: '120px' }}>Cantidad</th>
                        <th style={{ width: '120px' }}>Recibido</th>
                      </>
                    ) : (
                      <>
                        <th style={{ width: '60px' }}>C</th>
                        <th style={{ width: '60px' }}>D</th>
                        <th style={{ width: '60px' }}>U</th>
                        <th style={{ width: '80px' }}>Total</th>
                      </>
                    )}
                    {isSalidaTipo(selectedOrder.tipo) && <th style={{ width: '100px' }}>Stock</th>}
                    {isSalidaTipo(selectedOrder.tipo) && <th style={{ width: '250px' }}>Lotes / Picking</th>}
                    <th style={{ width: '100px' }}>Importe</th>
                    <th style={{ width: '120px' }}>Estado</th>
                    <th style={{ width: '130px' }}>Procesado por</th>
                    <th style={{ width: '150px' }}>Acciones</th>
                  </tr>
                  )}
                </thead>
                <tbody>
                  {selectedOrder.lineas?.map((linea, idx) => {
                    // Devolución rendering
                    if (selectedOrder.es_devolucion || isDevolucionTipo(selectedOrder.tipo)) {
                      const lr = linea.line_return || {}
                      const estadoRetornoColors = {
                        'pendiente': { background: '#fff3cd', color: '#856404' },
                        'en_proceso': { background: '#cce5ff', color: '#004085' },
                        'procesado': { background: '#d4edda', color: '#155724' },
                        'completado': { background: '#d4edda', color: '#155724' },
                        'cancelado': { background: '#f8d7da', color: '#721c24' }
                      }
                      const estadoProductoLabels = {
                        'bueno': 'Bueno',
                        'dañado': 'Dañado',
                        'caducado': 'Caducado',
                        'devolver_proveedor': 'Devolver a Proveedor'
                      }
                      return (
                        <tr key={idx}>
                          <td>
                            <div className="product-info">
                              <strong>{linea.producto_codigo}</strong>
                              <span>{linea.producto_descripcion}</span>
                            </div>
                          </td>
                          <td><strong>{lr.cantidad_max_retornada ?? linea.cantidad_max ?? 0}</strong></td>
                          <td><strong>{lr.cantidad_med_retornada ?? linea.cantidad_med ?? 0}</strong></td>
                          <td><strong>{lr.cantidad_peq_retornada ?? linea.cantidad_peq ?? 0}</strong></td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              ...(estadoRetornoColors[lr.estado] || { background: '#e2e3e5', color: '#383d41' })
                            }}>
                              {lr.estado ? lr.estado.replace('_', ' ') : 'Sin retorno'}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '13px' }}>
                              {lr.estado_producto ? (estadoProductoLabels[lr.estado_producto] || lr.estado_producto) : '-'}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '13px' }}>
                              {lr.lote_destino || 'Sin asignar'}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '13px' }}>
                              {lr.fecha_vencimiento_final || '-'}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '13px' }}>
                              {lr.ubicacion_destino || 'Sin asignar'}
                            </span>
                          </td>
                          <td>
                            {lr.processed_by_info ? (
                              <div style={{ fontSize: '12px' }}>
                                <div style={{ fontWeight: 600 }}>{lr.processed_by_info.nombre} {lr.processed_by_info.apellido}</div>
                                {lr.processed_by_info.fecha && (
                                  <div style={{ color: '#888', fontSize: '11px' }}>
                                    {new Date(lr.processed_by_info.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              {lr.estado === 'pendiente' && (
                                <button
                                  className="btn-action prepare"
                                  onClick={() => openRetornoModal(linea)}
                                  title="Iniciar Devolución"
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <RotateCcw size={14} />
                                  <span style={{ fontSize: '11px' }}>Iniciar</span>
                                </button>
                              )}
                              {lr.estado === 'en_proceso' && (
                                <button
                                  className="btn-action prepare"
                                  onClick={() => openRetornoModal(linea)}
                                  title="Continuar Devolución"
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#cce5ff', color: '#004085', border: '1px solid #b8daff' }}
                                >
                                  <Edit size={14} />
                                  <span style={{ fontSize: '11px' }}>Continuar</span>
                                </button>
                              )}
                              {lr.estado === 'procesado' && (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: '#d4edda',
                                  color: '#155724'
                                }}>
                                  Procesado
                                </span>
                              )}
                              {lr.estado === 'completado' && (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: '#d4edda',
                                  color: '#155724'
                                }}>
                                  Completado
                                </span>
                              )}
                              {lr.estado === 'cancelado' && (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: '#f8d7da',
                                  color: '#721c24'
                                }}>
                                  Cancelado
                                </span>
                              )}
                              {!lr.estado && (
                                <span style={{ fontSize: '12px', color: '#999' }}>—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    }

                    // Normal (non-devolución) rendering
                    let cantidadesMostrar = {
                      cajas: linea.cantidad_max || 0,
                      displays: linea.cantidad_med || 0,
                      unidades: linea.cantidad_peq || 0
                    };

                    if (linea.lotes_asignados && linea.lotes_asignados.length > 0) {
                      cantidadesMostrar = linea.lotes_asignados.reduce((acc, lote) => ({
                        cajas: acc.cajas + (lote.cantidad_max || 0),
                        displays: acc.displays + (lote.cantidad_med || 0),
                        unidades: acc.unidades + (lote.cantidad_peq || 0)
                      }), { cajas: 0, displays: 0, unidades: 0 });
                    }

                    return (
                      <tr key={idx}>
                        <td>
                          <div className="product-image-cell">
                            {linea.imagen_url ? (
                              <img src={linea.imagen_url} alt={linea.producto_descripcion} />
                            ) : (
                              <div className="no-image">
                                <ImageIcon size={16} />
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="product-info">
                            <strong>{linea.producto_codigo}</strong>
                            <span>{linea.producto_descripcion}</span>
                          </div>
                        </td>
                        {isEntradaTipo(selectedOrder.tipo) ? (
                          <>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span><strong>{linea.cantidad_solicitada}</strong> uds</span>
                                {(() => {
                                  const cm = linea.cantidad_display_por_unidad_mayor || 1;
                                  const cp = linea.cantidad_unidad_base_por_display || 1;
                                  if (cm <= 1 && cp <= 1) return null;
                                  const upc = cm * cp;
                                  const c = Math.floor(linea.cantidad_solicitada / upc);
                                  const r = linea.cantidad_solicitada % upc;
                                  const d = Math.floor(r / cp);
                                  const u = r % cp;
                                  const parts = [];
                                  if (c > 0) parts.push(`${c} ${linea.unidad_mayor || 'Caj'}`);
                                  if (d > 0) parts.push(`${d} ${linea.unidad_display || 'Dsp'}`);
                                  if (u > 0) parts.push(`${u} ${linea.unidad_base || 'Uds'}`);
                                  return (
                                    <span style={{ fontSize: '11px', color: '#6b7280' }} title={`1 ${linea.unidad_mayor || 'Caja'} = ${cm} ${linea.unidad_display || 'Display'} = ${upc} ${linea.unidad_base || 'Uds'}`}>
                                      ≈ {parts.join(' + ')}
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                            <td>
                              {(linea.cantidad_procesada != null && linea.cantidad_procesada > 0) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ fontWeight: 600 }}>
                                    {linea.cantidad_procesada}/{linea.cantidad_solicitada} uds
                                  </span>
                                  <div style={{
                                    width: '100%', height: '4px', background: '#e5e7eb', borderRadius: '2px'
                                  }}>
                                    <div style={{
                                      width: `${Math.min(100, (linea.cantidad_procesada / linea.cantidad_solicitada) * 100)}%`,
                                      height: '100%',
                                      background: linea.cantidad_procesada >= linea.cantidad_solicitada ? '#10B981' : '#3B82F6',
                                      borderRadius: '2px'
                                    }} />
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontSize: '12px' }}>Sin recibir</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{cantidadesMostrar.cajas}</td>
                            <td>{cantidadesMostrar.displays}</td>
                            <td>{cantidadesMostrar.unidades}</td>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <strong>{linea.cantidad_solicitada}</strong>
                                {(() => {
                                  const cm = linea.cantidad_display_por_unidad_mayor || 1;
                                  const cp = linea.cantidad_unidad_base_por_display || 1;
                                  if (cm <= 1 && cp <= 1) return null;
                                  return (
                                    <span style={{ fontSize: '10px', color: '#9ca3af' }} title={`1 ${linea.unidad_mayor || 'Caja'} = ${cm * cp} ${linea.unidad_base || 'Uds'}`}>
                                      1{linea.unidad_mayor ? linea.unidad_mayor.substring(0,3) : 'Caj'}={cm * cp}uds
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                          </>
                        )}
                        {isSalidaTipo(selectedOrder.tipo) && (
                          <td>
                            {linea.stock_info && (
                              <div className="stock-mini">
                                <span className="stock-value">{linea.stock_info.stockDisponible}</span>
                                <span className="stock-label">disp</span>
                              </div>
                            )}
                          </td>
                        )}
                        {isSalidaTipo(selectedOrder.tipo) && (
                          <td>
                            {linea.lotes_asignados && linea.lotes_asignados.length > 0 && (
                              <div className="lotes-inline">
                                {linea.lotes_asignados.map((lote, loteIdx) => (
                                  <div key={loteIdx} className="lote-inline-item">
                                    <div className="lote-inline-header">
                                      <Box size={10} />
                                      <strong>{lote.lote}</strong>
                                      <span className="lote-qty">{lote.stock_tomar}u</span>
                                    </div>
                                    <div className="lote-inline-details">
                                      <span className="lote-venc">Vence: {lote.fecha_vencimiento}</span>
                                      {lote.ubicaciones && lote.ubicaciones.length > 0 && (
                                        <div className="ubicaciones-mini">
                                          {lote.ubicaciones.map((ub, ubIdx) => (
                                            <span key={ubIdx} className="ubicacion-mini-tag">
                                              <MapPin size={8} />
                                              {ub.codigo_ubicacion}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {(linea.estado === 'en_preparacion' || linea.estado === 'completado') && 
                            linea.ubicaciones_picking && linea.ubicaciones_picking.length > 0 && (
                              <div className="picking-inline">
                                {linea.ubicaciones_picking.map((ub, ubIdx) => (
                                  <div key={ubIdx} className="picking-inline-item">
                                    <div className="picking-inline-header">
                                      <MapPin size={10} />
                                      <strong>{ub.codigo_ubicacion}</strong>
                                    </div>
                                    <div className="picking-inline-details">
                                      <span className="picking-lote-name">Lote: {ub.lote}</span>
                                      {ub.stock_movido > 0 && (
                                        <span className="picking-qty">Movido: {ub.stock_movido}u</span>
                                      )}
                                      {ub.ya_en_picking && (
                                        <span className="picking-badge-mini">Ya en picking</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        )}
                        <td>
                          <strong>S/{linea.total_linea}</strong>
                        </td>
                        <td>
                          <span className={`badge ${getEstadoBadgeClass(linea.estado)}`}>
                            {getEstadoIcon(linea.estado)}
                            {linea.estado.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {linea.scanned_by_info ? (
                            <div style={{ fontSize: '12px' }}>
                              <div style={{ fontWeight: 600 }}>{linea.scanned_by_info.nombre} {linea.scanned_by_info.apellido}</div>
                              {linea.scanned_by_info.fecha && (
                                <div style={{ color: '#888', fontSize: '11px' }}>
                                  {new Date(linea.scanned_by_info.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {/* ENTRADAS: Botón de completar (pendiente o parcialmente recibido) */}
                            {isEntradaTipo(selectedOrder.tipo) && linea.estado !== 'completado' && (
                              <button
                                className="btn-action complete"
                                onClick={() => handleCompletarLinea(linea)}
                                disabled={loadingLine === linea.id}
                                title={linea.cantidad_procesada > 0 ? "Continuar Recepcion" : "Completar Entrada"}
                              >
                                {loadingLine === linea.id ? (
                                  <RefreshCw size={14} className="spin" />
                                ) : (
                                  <CheckCircle size={14} />
                                )}
                              </button>
                            )}

                            {/* SALIDAS: Botones según estado */}
                            {isSalidaTipo(selectedOrder.tipo) && (
                              <>
                                {linea.estado === 'pendiente' && (
                                  <>
                                    <button
                                      className="btn-action prepare"
                                      onClick={() => handlePrepararLinea(linea)}
                                      disabled={loadingLine === linea.id}
                                      title="Reubicar a Picking"
                                    >
                                      {loadingLine === linea.id ? (
                                        <RefreshCw size={14} className="spin" />
                                      ) : (
                                        <MapPin size={14} />
                                      )}
                                    </button>
                                    <button
                                      className="btn-action complete"
                                      onClick={() => handleCompletarLinea(linea)}
                                      disabled={loadingLine === linea.id}
                                      title="Completar Directamente"
                                    >
                                      {loadingLine === linea.id ? (
                                        <RefreshCw size={14} className="spin" />
                                      ) : (
                                        <CheckCircle size={14} />
                                      )}
                                    </button>
                                  </>
                                )}

                                {linea.estado === 'en_preparacion' && (
                                  <button
                                    className="btn-action complete"
                                    onClick={() => handleCompletarLinea(linea)}
                                    disabled={loadingLine === linea.id}
                                    title="Completar"
                                  >
                                    {loadingLine === linea.id ? (
                                      <RefreshCw size={14} className="spin" />
                                    ) : (
                                      <CheckCircle size={14} />
                                    )}
                                  </button>
                                )}
                              </>
                            )}

                            {selectedOrder.estado === 'enviado' && (
                              <button
                                className="btn-action return"
                                onClick={() => handleOpenReturnModal(linea)}
                                disabled={loadingLine === linea.id}
                                title="Crear Retorno"
                              >
                                <RotateCcw size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DEBUG RAW AB */}
      {showDebugPanel && debugRawData && (
        <div className="modal-overlay" onClick={() => setShowDebugPanel(false)}>
          <div className="modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                  <Bug size={20} style={{ color: '#7c3aed' }} />
                  Debug Raw AB API - {activeTab}
                </h2>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  {debugRawData.endpoint && <span>Endpoint: <code>{debugRawData.endpoint}</code></span>}
                  {typeof debugRawData.count !== 'undefined' && <span style={{ marginLeft: '12px' }}>Count: <strong>{debugRawData.count}</strong></span>}
                </div>
              </div>
              <button className="btn-close" onClick={() => setShowDebugPanel(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(JSON.stringify(debugRawData, null, 2));
                      alert('JSON copiado al portapapeles');
                    } catch (err) {
                      alert('No se pudo copiar al portapapeles');
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', backgroundColor: '#2563EB', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontWeight: 600, fontSize: '13px'
                  }}
                >
                  <Code size={14} />
                  Copiar JSON
                </button>
              </div>
              <pre style={{
                maxHeight: '70vh', overflow: 'auto', background: '#1e293b', color: '#e2e8f0',
                padding: '16px', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace',
                margin: 0
              }}>
                {JSON.stringify(debugRawData, null, 2)}
              </pre>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDebugPanel(false)}
                style={{
                  padding: '8px 16px', backgroundColor: '#6B7280', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: 600, fontSize: '13px'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EXPORTACIÓN */}
      {showExportModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Exportar Pedido</h2>
              <button className="btn-close" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="export-info">
                <h3>{selectedOrder.codigo_pedido}</h3>
                <p>{selectedOrder.cliente_proveedor}</p>
                <p className="export-total">Total: S/{selectedOrder.total}</p>
              </div>

              <div className="export-options">
                <button 
                  className="export-option"
                  onClick={() => handleExportOrder('csv')}
                  disabled={exportLoading}
                >
                  <FileText size={32} />
                  <span>CSV</span>
                  <small>Tabla de datos</small>
                </button>

                <button 
                  className="export-option"
                  onClick={() => handleExportOrder('excel')}
                  disabled={exportLoading}
                >
                  <FileSpreadsheet size={32} />
                  <span>Excel</span>
                  <small>Hoja de cálculo</small>
                </button>

                <button 
                  className="export-option"
                  onClick={() => handleExportOrder('pdf')}
                  disabled={exportLoading}
                >
                  <File size={32} />
                  <span>PDF</span>
                  <small>Albarán imprimible</small>
                </button>
              </div>

              {exportLoading && (
                <div className="export-loading">
                  <RefreshCw size={20} className="spin" />
                  <span>Generando archivo...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🆕 MODAL DE COMPLETAR ENTRADA */}
      {showCompleteEntradaModal && selectedLineForComplete && (
        <div className="modal-overlay" onClick={() => { if (!loadingComplete) { setShowCompleteEntradaModal(false); setEntradaLotes([{ id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }]); setEntradaSubmitProgress(null); } }}>
          <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Completar Entrada</h2>
              <button className="btn-close" onClick={() => { if (!loadingComplete) { setShowCompleteEntradaModal(false); setEntradaLotes([{ id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }]); setEntradaSubmitProgress(null); } }}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <div className="return-product-info">
                <div className="product-image-cell">
                  {selectedLineForComplete.imagen_url ? (
                    <img src={selectedLineForComplete.imagen_url} alt={selectedLineForComplete.producto_descripcion} />
                  ) : (
                    <div className="no-image">
                      <ImageIcon size={24} />
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <strong>{selectedLineForComplete.producto_codigo}</strong>
                  <span>{selectedLineForComplete.producto_descripcion}</span>
                  <span className="qty-info">
                    Total solicitado: <strong>{selectedLineForComplete.cantidad_solicitada} uds</strong>
                  </span>
                  {selectedLineForComplete.cantidad_procesada > 0 && (
                    <span className="qty-info" style={{ color: '#3B82F6' }}>
                      Ya recibido: {selectedLineForComplete.cantidad_procesada}/{selectedLineForComplete.cantidad_solicitada} uds
                      — Faltan: {selectedLineForComplete.cantidad_solicitada - selectedLineForComplete.cantidad_procesada} uds
                    </span>
                  )}
                  {(() => {
                    const convMed = selectedLineForComplete.cantidad_display_por_unidad_mayor || 1;
                    const convPeq = selectedLineForComplete.cantidad_unidad_base_por_display || 1;
                    const parts = [];
                    if (convMed > 1) parts.push(`1 ${selectedLineForComplete.unidad_mayor || 'Caja'} = ${convMed} ${selectedLineForComplete.unidad_display || 'Displays'}`);
                    if (convPeq > 1) parts.push(`1 ${selectedLineForComplete.unidad_display || 'Display'} = ${convPeq} ${selectedLineForComplete.unidad_base || 'Uds'}`);
                    if (convMed > 1 && convPeq > 1) parts.push(`1 ${selectedLineForComplete.unidad_mayor || 'Caja'} = ${convMed * convPeq} ${selectedLineForComplete.unidad_base || 'Uds'}`);
                    if (parts.length === 0) return null;
                    return (
                      <span style={{ fontSize: '11px', color: '#718096' }}>
                        {parts.join(' · ')}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {selectedLineForComplete.cantidad_procesada > 0 && (
                <div className="info-box" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontWeight: 600, color: '#1D4ED8' }}>
                      Progreso: {selectedLineForComplete.cantidad_procesada}/{selectedLineForComplete.cantidad_solicitada} uds recibidas
                    </span>
                    <div style={{
                      width: '100%', height: '6px', background: '#DBEAFE', borderRadius: '3px'
                    }}>
                      <div style={{
                        width: `${Math.min(100, (selectedLineForComplete.cantidad_procesada / selectedLineForComplete.cantidad_solicitada) * 100)}%`,
                        height: '100%',
                        background: '#3B82F6',
                        borderRadius: '3px'
                      }} />
                    </div>
                  </div>
                </div>
              )}


              {entradaLotes.map((lote, idx) => (
                <div key={lote.id} style={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  background: idx % 2 === 0 ? '#FAFBFC' : '#F7FAFC',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#2D3748' }}>
                      Lote {idx + 1}
                    </span>
                    {entradaLotes.length > 1 && (
                      <button
                        onClick={() => handleRemoveLote(lote.id)}
                        disabled={loadingComplete}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#E53E3E', padding: '4px', borderRadius: '4px',
                          display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px'
                        }}
                      >
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Código de Lote *</label>
                      <input
                        type="text"
                        value={lote.lote}
                        onChange={(e) => handleLoteChange(lote.id, 'lote', e.target.value)}
                        placeholder="Ej: LOTE-2026-001"
                        disabled={loadingComplete}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Fecha de Vencimiento *</label>
                      <input
                        type="date"
                        value={lote.fecha_vencimiento}
                        onChange={(e) => handleLoteChange(lote.id, 'fecha_vencimiento', e.target.value)}
                        disabled={loadingComplete}
                      />
                    </div>
                  </div>

                  {(() => {
                    const levels = getProductUnitLevels();
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${levels.length}, 1fr)`, gap: '12px', marginBottom: '12px' }}>
                        {levels.map(level => (
                          <div className="form-group" key={level.key} style={{ margin: 0 }}>
                            <label>{level.label}</label>
                            <input
                              type="number"
                              min="0"
                              value={lote[level.field]}
                              onChange={(e) => handleLoteChange(lote.id, level.field, e.target.value)}
                              placeholder="0"
                              disabled={loadingComplete}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {(() => {
                    const udsBase = calcularUnidadesBase(lote);
                    if (udsBase === 0) return null;
                    return (
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', textAlign: 'right' }}>
                        = {udsBase} {selectedLineForComplete.unidad_base || 'unidades'} base
                      </div>
                    );
                  })()}

                  <div className="form-group" style={{ margin: '0 0 8px 0' }}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={lote.usar_ubicacion_recomendada}
                        onChange={(e) => handleLoteChange(lote.id, 'usar_ubicacion_recomendada', e.target.checked)}
                        disabled={loadingComplete}
                      />
                      <span>Usar ubicación recomendada (FEFO)</span>
                    </label>
                  </div>

                  {lote.lote && lote.fecha_vencimiento && lote.usar_ubicacion_recomendada && (
                    <div className="info-box" style={{ marginBottom: '0' }}>
                      <button
                        className="btn-secondary"
                        onClick={() => handleCalcUbicacionLote(lote)}
                        style={{ width: '100%' }}
                        disabled={loadingComplete}
                      >
                        <MapPin size={14} />
                        Calcular Ubicación Recomendada
                      </button>
                      {lote.ubicacionRecomendada && (
                        <div className="ubicacion-recomendada">
                          <CheckCircle size={16} color="#10B981" />
                          <span>Ubicación recomendada: <strong>{lote.ubicacionRecomendada}</strong></span>
                        </div>
                      )}
                    </div>
                  )}

                  {!lote.usar_ubicacion_recomendada && (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Ubicación Manual *</label>
                      <input
                        type="text"
                        value={lote.ubicacion_manual}
                        onChange={(e) => handleLoteChange(lote.id, 'ubicacion_manual', e.target.value)}
                        placeholder="Ej: A-01-01-N3"
                        disabled={loadingComplete}
                      />
                    </div>
                  )}
                </div>
              ))}

              <button
                onClick={handleAddLote}
                disabled={loadingComplete}
                style={{
                  width: '100%', padding: '10px', border: '2px dashed #CBD5E0',
                  borderRadius: '8px', background: 'transparent', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '8px', color: '#4A5568', fontSize: '14px', fontWeight: 500,
                  marginBottom: '16px', transition: 'all 0.2s'
                }}
              >
                <Plus size={16} />
                Agregar Lote
              </button>

              {(() => {
                const totalUnidadesBase = entradaLotes.reduce((sum, l) => sum + calcularUnidadesBase(l), 0);
                const restante = selectedLineForComplete.cantidad_solicitada - (selectedLineForComplete.cantidad_procesada || 0);
                const excede = totalUnidadesBase > restante;

                return (
                  <div style={{
                    background: excede ? '#FFF5F5' : '#F0FFF4',
                    border: `1px solid ${excede ? '#FED7D7' : '#C6F6D5'}`,
                    borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: excede ? '#C53030' : '#276749' }}>
                        Resumen ({entradaLotes.length} lote{entradaLotes.length > 1 ? 's' : ''})
                      </span>
                      <span style={{ fontSize: '13px', color: excede ? '#C53030' : '#2D3748', fontWeight: 600 }}>
                        {totalUnidadesBase} / {restante} uds base
                      </span>
                    </div>
                    {excede && (
                      <span style={{ fontSize: '12px', color: '#C53030' }}>
                        ⚠ Excede la cantidad solicitada en {totalUnidadesBase - restante} unidades
                      </span>
                    )}
                  </div>
                )
              })()}

              {entradaSubmitProgress && (
                <div style={{
                  background: '#EBF8FF', border: '1px solid #BEE3F8', borderRadius: '8px',
                  padding: '12px 16px', marginTop: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <RefreshCw size={14} className="spin" style={{ color: '#3182CE' }} />
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#2B6CB0' }}>
                      Procesando lote {entradaSubmitProgress.current}/{entradaSubmitProgress.total}: {entradaSubmitProgress.loteActual}
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '4px', background: '#BEE3F8', borderRadius: '2px' }}>
                    <div style={{
                      width: `${(entradaSubmitProgress.current / entradaSubmitProgress.total) * 100}%`,
                      height: '100%', background: '#3182CE', borderRadius: '2px', transition: 'width 0.3s'
                    }} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => { setShowCompleteEntradaModal(false); setEntradaLotes([{ id: Date.now(), lote: '', fecha_vencimiento: '', ubicacion_manual: '', usar_ubicacion_recomendada: true, cajas: '', displays: '', unidades: '', ubicacionRecomendada: null }]); setEntradaSubmitProgress(null); }}
                disabled={loadingComplete}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirmCompleteEntrada}
                disabled={loadingComplete}
              >
                {loadingComplete ? (
                  <>
                    <RefreshCw size={14} className="spin" />
                    {entradaSubmitProgress ? `Procesando ${entradaSubmitProgress.current}/${entradaSubmitProgress.total}...` : 'Completando...'}
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} />
                    Completar Entrada{entradaLotes.length > 1 ? ` (${entradaLotes.length} lotes)` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ MODAL DE COMPLETAR SALIDA - CORREGIDO CON LOTES FEFO */}
      {showCompleteSalidaModal && selectedLineForComplete && (
        <div className="modal-overlay" onClick={() => setShowCompleteSalidaModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Completar Salida</h2>
              <button className="btn-close" onClick={() => setShowCompleteSalidaModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="return-product-info">
                <div className="product-image-cell">
                  {selectedLineForComplete.imagen_url ? (
                    <img src={selectedLineForComplete.imagen_url} alt={selectedLineForComplete.producto_descripcion} />
                  ) : (
                    <div className="no-image">
                      <ImageIcon size={24} />
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <strong>{selectedLineForComplete.producto_codigo}</strong>
                  <span>{selectedLineForComplete.producto_descripcion}</span>
                  <span className="qty-info">
                    Cantidad solicitada: {selectedLineForComplete.cantidad_solicitada} uds
                  </span>
                </div>
              </div>

              <div className="info-box warning">
                <AlertCircle size={16} />
                <div>
                  <p><strong>¿Confirmar completar esta salida?</strong></p>
                  <p style={{ fontSize: '13px', marginTop: '8px' }}>
                    Se reducirá el stock del almacén según los lotes FEFO asignados.
                    {lotesFefoParaSalida && lotesFefoParaSalida.length > 0 && (
                      <span> ({lotesFefoParaSalida.length} lote(s) asignado(s))</span>
                    )}
                  </p>
                </div>
              </div>

              {/* ✅ MOSTRAR LOTES FEFO */}
              {lotesFefoParaSalida && lotesFefoParaSalida.length > 0 && (
                <div className="lotes-summary">
                  <h4>Lotes FEFO Asignados:</h4>
                  {lotesFefoParaSalida.map((lote, idx) => (
                    <div key={idx} className="lote-summary-item">
                      <Box size={14} style={{ flexShrink: 0, color: '#4299e1' }} />
                      <span style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <strong style={{ color: '#2d3748' }}>{lote.lote}</strong> - {lote.stock_tomar} uds 
                        <span style={{ color: '#718096', marginLeft: '8px' }}>
                          (Vence: {new Date(lote.fecha_vencimiento).toLocaleDateString('es-ES')})
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowCompleteSalidaModal(false)}
                disabled={loadingComplete}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirmCompleteSalida}
                disabled={loadingComplete}
              >
                {loadingComplete ? (
                  <>
                    <RefreshCw size={14} className="spin" />
                    Completando...
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} />
                    Confirmar y Completar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE RETORNO */}
      {showReturnModal && selectedLineForReturn && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal return-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Crear Retorno</h2>
              <button className="btn-close" onClick={() => setShowReturnModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="return-product-info">
                <div className="product-image-cell">
                  {selectedLineForReturn.imagen_url ? (
                    <img src={selectedLineForReturn.imagen_url} alt={selectedLineForReturn.producto_descripcion} />
                  ) : (
                    <div className="no-image">
                      <ImageIcon size={24} />
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <strong>{selectedLineForReturn.producto_codigo}</strong>
                  <span>{selectedLineForReturn.producto_descripcion}</span>
                  <span className="qty-info">
                    Cantidad solicitada: {selectedLineForReturn.cantidad_max}C {selectedLineForReturn.cantidad_med}D {selectedLineForReturn.cantidad_peq}U 
                    ({selectedLineForReturn.cantidad_solicitada} uds)
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Cajas a Retornar</label>
                <input
                  type="number"
                  min="0"
                  max={selectedLineForReturn.cantidad_max}
                  value={returnData.cantidad_max_retornada || ''}
                  onChange={(e) => setReturnData({...returnData, cantidad_max_retornada: e.target.value})}
                  placeholder="Ej: 5"
                />
              </div>

              <div className="form-group">
                <label>Displays a Retornar</label>
                <input
                  type="number"
                  min="0"
                  max={selectedLineForReturn.cantidad_med}
                  value={returnData.cantidad_med_retornada || ''}
                  onChange={(e) => setReturnData({...returnData, cantidad_med_retornada: e.target.value})}
                  placeholder="Ej: 3"
                />
              </div>

              <div className="form-group">
                <label>Unidades a Retornar</label>
                <input
                  type="number"
                  min="0"
                  max={selectedLineForReturn.cantidad_peq}
                  value={returnData.cantidad_peq_retornada || ''}
                  onChange={(e) => setReturnData({...returnData, cantidad_peq_retornada: e.target.value})}
                  placeholder="Ej: 10"
                />
              </div>

              <div className="form-group">
                <label>Motivo del Retorno *</label>
                <textarea
                  value={returnData.motivo}
                  onChange={(e) => setReturnData({...returnData, motivo: e.target.value})}
                  placeholder="Describa el motivo del retorno..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Estado del Producto *</label>
                <select
                  value={returnData.estado_producto}
                  onChange={(e) => setReturnData({...returnData, estado_producto: e.target.value})}
                >
                  <option value="bueno">Bueno (se reubicará automáticamente)</option>
                  <option value="dañado">Dañado (requiere revisión)</option>
                  <option value="caducado">Caducado (requiere revisión)</option>
                </select>
              </div>

              {returnData.estado_producto === 'bueno' && (
                <div className="info-box success">
                  <CheckCircle size={16} />
                  <span>El retorno se procesará en la app móvil mediante escaneo de productos</span>
                </div>
              )}

              {returnData.estado_producto !== 'bueno' && (
                <div className="info-box warning">
                  <AlertCircle size={16} />
                  <span>El producto requerirá revisión manual antes de ser reubicado</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowReturnModal(false)}
                disabled={loadingReturn}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCreateReturn}
                disabled={loadingReturn}
              >
                {loadingReturn ? (
                  <>
                    <RefreshCw size={14} className="spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <RotateCcw size={14} />
                    Crear Retorno
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* MODAL PROCESAR DEVOLUCIÓN (multi-step) */}
      {showRetornoModal && (
        <div className="modal-overlay" onClick={() => setShowRetornoModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '95%' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0 }}>Procesar Devolución</h2>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  {[1, 2, 3, 4].map(step => (
                    <div key={step} style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                      background: retornoModalStep === step ? '#3B82F6' : retornoModalStep > step ? '#10B981' : '#e2e3e5',
                      color: retornoModalStep >= step ? '#fff' : '#666',
                      transition: 'all 0.2s'
                    }}>
                      {retornoModalStep > step ? '✓' : step}
                    </div>
                  ))}
                </div>
              </div>
              <button className="btn-close" onClick={() => setShowRetornoModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Product info header - always visible */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>{retornoModalData.productCode}</div>
                <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>{retornoModalData.productDesc}</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  {retornoModalData.cantidades.cajas}C / {retornoModalData.cantidades.displays}D / {retornoModalData.cantidades.unidades}U
                </div>
              </div>

              {/* STEP 1: Lote + Fecha */}
              {retornoModalStep === 1 && (
                <div>
                  <h3 style={{ fontSize: '15px', marginBottom: '12px', color: '#333' }}>Lote y Fecha de Vencimiento</h3>
                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px', display: 'block' }}>Número de Lote *</label>
                    <input
                      type="text"
                      value={retornoModalData.lote}
                      onChange={(e) => setRetornoModalData(prev => ({ ...prev, lote: e.target.value }))}
                      placeholder="Ej: LOT-2024-001"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px', display: 'block' }}>Fecha de Vencimiento *</label>
                    <input
                      type="date"
                      value={retornoModalData.fechaVencimiento}
                      onChange={(e) => setRetornoModalData(prev => ({ ...prev, fechaVencimiento: e.target.value }))}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    />
                  </div>
                </div>
              )}

              {/* STEP 2: Estado Producto */}
              {retornoModalStep === 2 && (
                <div>
                  <h3 style={{ fontSize: '15px', marginBottom: '12px', color: '#333' }}>Estado del Producto</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {[
                      { value: 'bueno', label: 'Bueno', icon: '✓', color: '#10B981', bg: '#d1fae5', border: '#6ee7b7', desc: 'Producto en buen estado, se reubicará en almacén' },
                      { value: 'dañado', label: 'Dañado', icon: '⚠', color: '#F59E0B', bg: '#fef3c7', border: '#fcd34d', desc: 'Producto dañado, no ingresa al almacén' },
                      { value: 'caducado', label: 'Caducado', icon: '🕐', color: '#EF4444', bg: '#fee2e2', border: '#fca5a5', desc: 'Producto caducado, no ingresa al almacén' },
                      { value: 'devolver_proveedor', label: 'Devolver a Proveedor', icon: '↩', color: '#3B82F6', bg: '#dbeafe', border: '#93c5fd', desc: 'Se devuelve al proveedor, no ingresa al almacén' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setRetornoModalData(prev => ({ ...prev, estadoProducto: opt.value }))}
                        style={{
                          padding: '14px 12px',
                          borderRadius: '8px',
                          border: `2px solid ${retornoModalData.estadoProducto === opt.value ? opt.color : '#e5e7eb'}`,
                          background: retornoModalData.estadoProducto === opt.value ? opt.bg : '#fff',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.15s'
                        }}
                      >
                        <div style={{ fontSize: '22px', marginBottom: '4px' }}>{opt.icon}</div>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: opt.color }}>{opt.label}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', lineHeight: '1.3' }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 3: Ubicación (solo para bueno) */}
              {retornoModalStep === 3 && (
                <div>
                  <h3 style={{ fontSize: '15px', marginBottom: '12px', color: '#333' }}>Ubicación Destino</h3>
                  {retornoModalData.fefoRecommendation && (
                    <div style={{
                      background: '#dbeafe',
                      border: '1px solid #93c5fd',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '14px'
                    }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#1e40af', marginBottom: '4px' }}>
                        <MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                        Recomendación FEFO
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af' }}>
                        {retornoModalData.fefoRecommendation.ubicacion}
                      </div>
                      <button
                        onClick={() => setRetornoModalData(prev => ({ ...prev, ubicacion: prev.fefoRecommendation.ubicacion }))}
                        style={{
                          marginTop: '8px',
                          padding: '6px 14px',
                          background: '#3B82F6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Usar recomendación FEFO
                      </button>
                    </div>
                  )}
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px', display: 'block' }}>
                      {retornoModalData.fefoRecommendation && retornoModalData.ubicacion === retornoModalData.fefoRecommendation.ubicacion
                        ? 'Ubicación asignada por FEFO'
                        : 'Ubicación manual'}
                    </label>
                    <input
                      type="text"
                      value={retornoModalData.ubicacion}
                      onChange={(e) => setRetornoModalData(prev => ({ ...prev, ubicacion: e.target.value }))}
                      placeholder="Ej: A-01-02-03"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    />
                  </div>
                </div>
              )}

              {/* STEP 4: Confirmación */}
              {retornoModalStep === 4 && (
                <div>
                  <h3 style={{ fontSize: '15px', marginBottom: '12px', color: '#333' }}>Confirmar Datos</h3>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '14px',
                    border: '1px solid #e9ecef',
                    display: 'grid',
                    gap: '10px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Producto</span>
                      <span style={{ fontWeight: 600 }}>{retornoModalData.productCode} - {retornoModalData.productDesc}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Cantidades</span>
                      <span style={{ fontWeight: 600 }}>{retornoModalData.cantidades.cajas}C / {retornoModalData.cantidades.displays}D / {retornoModalData.cantidades.unidades}U</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Lote</span>
                      <span style={{ fontWeight: 600 }}>{retornoModalData.lote}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Fecha Vencimiento</span>
                      <span style={{ fontWeight: 600 }}>{retornoModalData.fechaVencimiento}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', alignItems: 'center' }}>
                      <span style={{ color: '#666' }}>Estado Producto</span>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 700,
                        background: retornoModalData.estadoProducto === 'bueno' ? '#d1fae5' :
                                   retornoModalData.estadoProducto === 'dañado' ? '#fef3c7' :
                                   retornoModalData.estadoProducto === 'caducado' ? '#fee2e2' : '#dbeafe',
                        color: retornoModalData.estadoProducto === 'bueno' ? '#065f46' :
                               retornoModalData.estadoProducto === 'dañado' ? '#92400e' :
                               retornoModalData.estadoProducto === 'caducado' ? '#991b1b' : '#1e40af'
                      }}>
                        {retornoModalData.estadoProducto === 'bueno' ? 'Bueno' :
                         retornoModalData.estadoProducto === 'dañado' ? 'Dañado' :
                         retornoModalData.estadoProducto === 'caducado' ? 'Caducado' : 'Devolver a Proveedor'}
                      </span>
                    </div>
                    {retornoModalData.estadoProducto === 'bueno' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#666' }}>Ubicación</span>
                        <span style={{ fontWeight: 600 }}>{retornoModalData.ubicacion}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <div>
                {retornoModalStep > 1 && (
                  <button
                    className="btn-secondary"
                    onClick={handleRetornoStepBack}
                    disabled={retornoModalLoading}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <ChevronLeft size={14} />
                    Atrás
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn-secondary"
                  onClick={() => setShowRetornoModal(false)}
                  disabled={retornoModalLoading}
                >
                  Cancelar
                </button>
                {retornoModalStep < 4 ? (
                  <button
                    className="btn-primary"
                    onClick={handleRetornoStepNext}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Siguiente
                    <ChevronLeft size={14} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleRetornoSubmit}
                    disabled={retornoModalLoading}
                    style={{ background: '#10B981', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    {retornoModalLoading ? (
                      <>
                        <RefreshCw size={14} className="spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} />
                        Confirmar
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL UPLOAD */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cargar Pedidos desde CSV</h2>
              <button className="btn-close" onClick={() => setShowUploadModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="download-examples">
                <p><strong>Descargar plantillas de ejemplo:</strong></p>
                <div className="example-buttons">
                  <a
                    href={`${API_URL}/orders/download-example/entrada`}
                    download="entrada_ejemplo.csv"
                    className="btn-example"
                  >
                    <Download size={14} />
                    Entrada
                  </a>
                  <a
                    href={`${API_URL}/orders/download-example/salida`}
                    download="salida_ejemplo.csv"
                    className="btn-example"
                  >
                    <Download size={14} />
                    Salida
                  </a>
                </div>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv"
                style={{ display: 'none' }}
              />

              <button
                className="btn-upload-file"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={20} />
                {csvFile ? csvFile.name : 'Seleccionar archivo CSV'}
              </button>

              {parsedCSV && (
                <div className="parsed-info">
                  <CheckCircle size={16} color="#10B981" />
                  <span>{parsedCSV.length} líneas cargadas</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleGeneratePreview}
                disabled={!parsedCSV || uploadLoading}
              >
                {uploadLoading ? 'Procesando...' : 'Generar Vista Previa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VISTA PREVIA PICKING */}
      {showPreviewModal && previewPicking && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Vista Previa de Reubicación a Picking</h2>
              <button className="btn-close" onClick={() => setShowPreviewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="preview-product-info">
                <div className="product-image-preview">
                  {previewPicking.producto.imagen_url ? (
                    <img src={previewPicking.producto.imagen_url} alt={previewPicking.producto.descripcion} />
                  ) : (
                    <div className="no-image-large">
                      <ImageIcon size={32} />
                    </div>
                  )}
                </div>
                <div className="product-details-preview">
                  <h3>{previewPicking.producto.codigo}</h3>
                  <p>{previewPicking.producto.descripcion}</p>
                  <div className="product-units">
                    <span>{previewPicking.producto.unidad_mayor} = {previewPicking.producto.conversion_med} {previewPicking.producto.unidad_display}</span>
                    <span>{previewPicking.producto.unidad_display} = {previewPicking.producto.conversion_peq} {previewPicking.producto.unidad_base}</span>
                  </div>
                  <div className="preview-cantidad">
                    <strong>Cantidad a preparar:</strong> {previewPicking.cantidad_solicitada} unidades
                  </div>
                </div>
              </div>

              {previewPicking.stock_info && (
                <div className="stock-info-preview">
                  <div className="stock-info-item">
                    <span>Total:</span>
                    <strong>{previewPicking.stock_info.total} uds</strong>
                  </div>
                  <div className="stock-info-item warning">
                    <span>Reservado:</span>
                    <strong>{previewPicking.stock_info.reservado} uds</strong>
                  </div>
                  <div className="stock-info-item success">
                    <span>Disponible:</span>
                    <strong>{previewPicking.stock_info.disponible} uds</strong>
                  </div>
                </div>
              )}

              <div className="modo-selector">
                <button
                  className={`modo-btn ${modoReubicacion === 'automatico' ? 'active' : ''}`}
                  onClick={() => setModoReubicacion('automatico')}
                >
                  Automático
                </button>
                <button
                  className={`modo-btn ${modoReubicacion === 'manual' ? 'active' : ''}`}
                  onClick={() => setModoReubicacion('manual')}
                >
                  <Edit size={14} />
                  Manual
                </button>
              </div>

              <div className="preview-resumen">
                <div className="resumen-item">
                  <span className="label">Stock ya en picking:</span>
                  <span className="value success">{previewPicking.resumen.stock_ya_en_picking} uds</span>
                </div>
                <div className="resumen-item">
                  <span className="label">Stock a mover:</span>
                  <span className="value warning">{previewPicking.resumen.stock_a_mover_de_almacenaje} uds</span>
                </div>
                <div className="resumen-item">
                  <span className="label">Lotes afectados:</span>
                  <span className="value">{previewPicking.resumen.lotes_afectados}</span>
                </div>
              </div>

              <h4>Movimientos a Realizar</h4>
              <div className="movimientos-container">
                {previewPicking.movimientos.map((mov, idx) => (
                  <div key={idx} className="movimiento-card">
                    <div className="movimiento-header">
                      <div>
                        <strong>Lote: {mov.lote}</strong>
                        <br />
                        <small>Vence: {mov.fecha_vencimiento}</small>
                      </div>
                      <span className="cantidad-tag">Total: {mov.stock_total_necesario} uds</span>
                    </div>

                    {mov.stock_ya_en_picking > 0 && (
                      <div className="stock-existente">
                        <CheckCircle size={16} />
                        <span>{mov.stock_ya_en_picking} uds ya disponibles en picking</span>
                      </div>
                    )}

                    {mov.stock_a_mover > 0 && (
                      <div className="movimiento-detalle">
                        <div className="origen-section">
                          <h5>
                            <MapPin size={14} />
                            Origen (Almacenaje)
                          </h5>
                          {mov.origen.map((orig, origIdx) => (
                            <div key={origIdx} className="ubicacion-origen">
                              <span className="ubicacion-codigo">{orig.codigo}</span>
                              <div className="stock-info">
                                <div>Actual: {orig.cantidad_max_actual}C + {orig.cantidad_med_actual}D + {orig.cantidad_peq_actual}U = {orig.stock_actual} uds</div>
                                {modoReubicacion === 'automatico' && (
                                  <>
                                    <div className="mover">Mover: {orig.cantidad_max_a_mover}C + {orig.cantidad_med_a_mover}D + {orig.cantidad_peq_a_mover}U = {orig.stock_a_mover} uds</div>
                                    <div>Quedará: {orig.cantidad_max_quedara}C + {orig.cantidad_med_quedara}D + {orig.cantidad_peq_quedara}U = {orig.stock_quedara} uds</div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {modoReubicacion === 'manual' && (
                          <div className="manual-inputs-section">
                            <h5>
                              <Edit size={14} />
                              Cantidades a Mover (Manual)
                            </h5>
                            <div className="manual-inputs">
                              <div className="input-group">
                                <label>Cajas:</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={reubicacionesManuales[mov.lote]?.cantidad_max ?? mov.cantidad_max}
                                  onChange={(e) => handleCambiarCantidadManual(mov.lote, 'cantidad_max', e.target.value)}
                                />
                              </div>
                              <div className="input-group">
                                <label>Displays:</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={reubicacionesManuales[mov.lote]?.cantidad_med ?? mov.cantidad_med}
                                  onChange={(e) => handleCambiarCantidadManual(mov.lote, 'cantidad_med', e.target.value)}
                                />
                              </div>
                              <div className="input-group">
                                <label>Unidades:</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={reubicacionesManuales[mov.lote]?.cantidad_peq ?? mov.cantidad_peq}
                                  onChange={(e) => handleCambiarCantidadManual(mov.lote, 'cantidad_peq', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="destino-section">
                          <h5>
                            <Package size={14} />
                            Destino (Picking)
                          </h5>
                          {modoReubicacion === 'manual' && mov.destino?.niveles_disponibles ? (
                            <div className="nivel-selector">
                              <label>Seleccionar Nivel:</label>
                              <select
                                value={reubicacionesManuales[mov.lote]?.nivel_id ?? mov.destino.nivel_id}
                                onChange={(e) => handleCambiarNivelManual(mov.lote, e.target.value)}
                              >
                                {mov.destino.niveles_disponibles.map(nivel => (
                                  <option key={nivel.id} value={nivel.id}>
                                    Nivel {nivel.numero}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="ubicacion-destino">
                              <span className="ubicacion-codigo picking">
                                {mov.destino?.codigo}
                              </span>
                              {modoReubicacion === 'automatico' && (
                                <span className="cantidad-mover">
                                  + {mov.cantidad_max}C + {mov.cantidad_med}D + {mov.cantidad_peq}U = {mov.stock_a_mover} uds
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {mov.nota && (
                      <div className="movimiento-nota">
                        <AlertCircle size={14} />
                        {mov.nota}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowPreviewModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirmarPreparacion}
                disabled={loadingLine === selectedLineForPreview?.id}
              >
                {loadingLine === selectedLineForPreview?.id ? (
                  'Procesando...'
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Confirmar Reubicación
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PWMS modals removed — replaced by auto-sync */}

      <style jsx>{`

        .order-management {
          min-height: 100vh;
          background: #FAFAFA;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .page-header {
          background: white;
          border-bottom: 1px solid #E5E7EB;
        }

        .header-content {
          max-width: 1600px;
          margin: 0 auto;
          padding: 20px 24px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .title-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .title-icon {
          color: #3B82F6;
          width: 28px;
          height: 28px;
        }

        .title-section h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .btn-upload {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .btn-upload:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .tabs {
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          gap: 32px;
          margin-top: 16px;
        }

        .tab {
          position: relative;
          padding: 12px 0;
          border: none;
          background: none;
          color: #64748B;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s;
        }

        .tab::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: transparent;
          transition: background 0.2s;
        }

        .tab.active {
          color: #1A1A1A;
        }

        .tab.active::after {
          background: #3B82F6;
        }

        .tab:hover:not(.active) {
          color: #3B82F6;
        }

        .detail-actions {
          display: flex;
          gap: 12px;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: white;
          border: 2px solid #3B82F6;
          border-radius: 6px;
          color: #3B82F6;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-export:hover {
          background: #3B82F6;
          color: white;
          transform: translateY(-1px);
        }

        .btn-ship {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #6366F1;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-ship:hover {
          background: #4F46E5;
          transform: translateY(-1px);
        }

        .export-modal {
          max-width: 500px;
        }

        .export-info {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #E5E7EB;
        }

        .export-info h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .export-info p {
          margin: 0;
          font-size: 14px;
          color: #64748B;
        }

        .export-total {
          margin-top: 12px !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          color: #3B82F6 !important;
        }

        .export-options {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .export-option {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 24px 12px;
          background: white;
          border: 2px solid #E5E7EB;
          border-radius: 8px;
          color: #3B82F6;
          cursor: pointer;
          transition: all 0.2s;
        }

        .export-option:hover:not(:disabled) {
          border-color: #3B82F6;
          background: #EFF6FF;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }

        .export-option:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .export-option span {
          font-size: 14px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .export-option small {
          font-size: 11px;
          color: #64748B;
        }

        .export-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 20px;
          padding: 16px;
          background: #F0FDF4;
          border: 1px solid #BBF7D0;
          border-radius: 6px;
          color: #166534;
          font-size: 14px;
          font-weight: 500;
        }

        .list-container {
          max-width: 1600px;
          margin: 0 auto;
          padding: 24px;
        }

        .filters-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .search-box {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
        }

        .search-box svg {
          color: #9CA3AF;
          flex-shrink: 0;
        }

        .search-box input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 14px;
          color: #1A1A1A;
        }

        .filter-select {
          padding: 10px 14px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          font-size: 14px;
          color: #1A1A1A;
          cursor: pointer;
          min-width: 180px;
        }

        .table-wrapper {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow: hidden;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #F8FAFC;
          border-bottom: 1px solid #E5E7EB;
        }

        .data-table th {
          padding: 12px 16px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .data-table tbody tr {
          border-bottom: 1px solid #F1F5F9;
          transition: background 0.2s;
        }

        .data-table tbody tr:last-child {
          border-bottom: none;
        }

        .data-table tbody tr.clickable-row {
          cursor: pointer;
        }

        .data-table tbody tr:hover {
          background: #F9FAFB;
        }

        .data-table td {
          padding: 12px 16px;
          font-size: 13px;
          color: #1A1A1A;
        }

        .code-cell {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: #3B82F6;
        }

        .product-image-cell {
          width: 48px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E5E7EB;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-image-cell img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #CBD5E1;
        }

        .product-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .product-info strong {
          font-size: 13px;
          color: #1A1A1A;
        }

        .product-info span {
          font-size: 11px;
          color: #64748B;
        }

        .stock-mini {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stock-value {
          font-size: 14px;
          font-weight: 700;
          color: #10B981;
        }

        .stock-label {
          font-size: 10px;
          color: #64748B;
          text-transform: uppercase;
        }

        .ubicacion-cell {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #3B82F6;
          font-weight: 500;
        }

        .motivo-cell {
          max-width: 200px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .text-muted {
          color: #94A3B8;
          font-style: italic;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .badge-pendiente {
          background: #FEF3C7;
          color: #92400E;
        }

        .badge-preparacion {
          background: #DBEAFE;
          color: #1E40AF;
        }

        .badge-completado {
          background: #D1FAE5;
          color: #065F46;
        }

        .badge-enviado {
          background: #E0E7FF;
          color: #3730A3;
        }

        .badge-retornado-completo {
          background: #FCE7F3;
          color: #831843;
        }

        .badge-retornado-parcial {
          background: #FEF3C7;
          color: #92400E;
        }

        .badge-procesado {
          background: #D1FAE5;
          color: #065F46;
        }

        .badge-bueno {
          background: #D1FAE5;
          color: #065F46;
        }

        .badge-dañado {
          background: #FEE2E2;
          color: #991B1B;
        }

        .badge-caducado {
          background: #FED7D7;
          color: #7F1D1D;
        }

        .badge-ok {
          background: #D1FAE5;
          color: #065F46;
        }

        .badge-warning {
          background: #FEF3C7;
          color: #92400E;
        }

        .badge-error {
          background: #FEE2E2;
          color: #991B1B;
        }

        .no-data {
          padding: 60px 20px;
          text-align: center;
          color: #94A3B8;
        }

        .no-data svg {
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .no-data p {
          margin: 0;
          font-size: 14px;
        }

        .btn-icon {
          padding: 6px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          color: #3B82F6;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-icon:hover {
          background: #F9FAFB;
          border-color: #3B82F6;
        }

        .detail-container,
        .preview-container {
          max-width: 1600px;
          margin: 0 auto;
          padding: 24px;
        }

        .detail-header,
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .detail-header h2,
        .preview-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          color: #64748B;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-back:hover {
          background: #F9FAFB;
          color: #1A1A1A;
          border-color: #D1D5DB;
        }

        .btn-confirm {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #10B981;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-confirm:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .info-section {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #F1F5F9;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .info-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          font-size: 14px;
          color: #1A1A1A;
          font-weight: 500;
        }

        .capitalize {
          text-transform: capitalize;
        }

        .totals-grid {
          display: flex;
          justify-content: flex-end;
          gap: 24px;
        }

        .total-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: flex-end;
        }

        .total-item span {
          font-size: 11px;
          color: #64748B;
          text-transform: uppercase;
        }

        .total-item strong {
          font-size: 16px;
          color: #1A1A1A;
        }

        .total-item.highlight strong {
          font-size: 18px;
          color: #3B82F6;
        }

        .lines-section {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 20px;
        }

        .lines-section h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .lines-table .action-buttons {
          display: flex;
          gap: 6px;
        }

        .btn-action {
          padding: 6px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-action.prepare {
          background: #DBEAFE;
          color: #1E40AF;
        }

        .btn-action.prepare:hover:not(:disabled) {
          background: #BFDBFE;
        }

        .btn-action.complete {
          background: #D1FAE5;
          color: #065F46;
        }

        .btn-action.complete:hover:not(:disabled) {
          background: #A7F3D0;
        }

        .btn-action.return {
          background: #FEF3C7;
          color: #92400E;
        }

        .btn-action.return:hover:not(:disabled) {
          background: #FDE68A;
        }

        .btn-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .lotes-inline,
        .picking-inline {
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-height: 200px;
          overflow-y: auto;
        }

        .lote-inline-item,
        .picking-inline-item {
          padding: 6px 8px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          font-size: 11px;
        }

        .lote-inline-header,
        .picking-inline-header {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 4px;
        }

        .lote-inline-header svg,
        .picking-inline-header svg {
          color: #3B82F6;
          flex-shrink: 0;
        }

        .lote-inline-header strong,
        .picking-inline-header strong {
          font-size: 11px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .lote-qty,
        .picking-qty {
          margin-left: auto;
          font-size: 10px;
          color: #64748B;
          font-weight: 500;
        }

        .lote-inline-details,
        .picking-inline-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding-left: 14px;
        }

        .lote-venc,
        .picking-lote-name {
          font-size: 10px;
          color: #64748B;
        }

        .ubicaciones-mini {
          display: flex;
          flex-wrap: wrap;
          gap: 3px;
          margin-top: 3px;
        }

        .ubicacion-mini-tag {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 2px 4px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 2px;
          font-size: 9px;
          color: #3B82F6;
          font-weight: 500;
        }

        .ubicacion-mini-tag svg {
          width: 8px;
          height: 8px;
        }

        .picking-badge-mini {
          display: inline-block;
          padding: 2px 5px;
          background: #DBEAFE;
          color: #1E40AF;
          border-radius: 3px;
          font-size: 9px;
          font-weight: 500;
          margin-top: 2px;
        }

        .preview-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .preview-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #F1F5F9;
        }

        .preview-card-header h3 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .preview-card-header p {
          margin: 0;
          font-size: 13px;
          color: #64748B;
        }

        .preview-stats {
          display: flex;
          gap: 8px;
        }

        .stat-ok,
        .stat-warning,
        .stat-error {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }

        .stat-ok {
          background: #D1FAE5;
          color: #065F46;
        }

        .stat-warning {
          background: #FEF3C7;
          color: #92400E;
        }

        .stat-error {
          background: #FEE2E2;
          color: #991B1B;
        }

        .data-table.compact {
          font-size: 12px;
        }

        .data-table.compact th,
        .data-table.compact td {
          padding: 10px 12px;
        }

        .errors-box {
          margin-top: 12px;
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 6px;
        }

        .error-item,
        .warning-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          font-size: 12px;
        }

        .error-item {
          color: #991B1B;
        }

        .warning-item {
          color: #92400E;
        }

        .error-item:last-child,
        .warning-item:last-child {
          margin-bottom: 0;
        }

        .text-error {
          color: #DC2626;
          font-size: 11px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        }

        .modal-large {
          max-width: 1000px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #E5E7EB;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .btn-close {
          padding: 4px;
          background: none;
          border: none;
          color: #6B7280;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .btn-close:hover {
          color: #1A1A1A;
          background: #F3F4F6;
        }

        .modal-body {
          padding: 20px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 20px;
          border-top: 1px solid #E5E7EB;
        }

        .btn-secondary {
          padding: 10px 16px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          color: #64748B;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: #F9FAFB;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3B82F6;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2563EB;
        }

        .btn-primary:disabled {
          background: #9CA3AF;
          cursor: not-allowed;
        }

        .preview-product-info {
          display: flex;
          gap: 16px;
          margin-bottom: 20px;
          padding: 16px;
          background: #F9FAFB;
          border-radius: 6px;
        }

        .product-image-preview {
          width: 80px;
          height: 80px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #E5E7EB;
          flex-shrink: 0;
        }

        .product-image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-image-large {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #CBD5E1;
        }

        .product-details-preview h3 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .product-details-preview p {
          margin: 0 0 10px 0;
          font-size: 13px;
          color: #64748B;
        }

        .product-units {
          display: flex;
          gap: 16px;
          margin-bottom: 8px;
          font-size: 11px;
          color: #6B7280;
        }

        .preview-cantidad {
          font-size: 13px;
        }

        .preview-cantidad strong {
          color: #1A1A1A;
        }

        .stock-info-preview {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          padding: 14px;
          background: #F0FDF4;
          border: 1px solid #BBF7D0;
          border-radius: 6px;
        }

        .stock-info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stock-info-item span {
          font-size: 11px;
          color: #166534;
          text-transform: uppercase;
          font-weight: 600;
        }

        .stock-info-item strong {
          font-size: 16px;
          color: #166534;
        }

        .stock-info-item.warning strong {
          color: #F59E0B;
        }

        .stock-info-item.success strong {
          color: #10B981;
        }

        .modo-selector {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
          padding: 4px;
          background: #F3F4F6;
          border-radius: 6px;
        }

        .modo-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          background: white;
          border: 1px solid transparent;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #6B7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modo-btn:hover {
          color: #1A1A1A;
        }

        .modo-btn.active {
          background: #2563EB;
          color: white;
          border-color: #2563EB;
        }

        .preview-resumen {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
          padding: 14px;
          background: #F0FDF4;
          border: 1px solid #BBF7D0;
          border-radius: 6px;
        }

        .resumen-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .resumen-item .label {
          font-size: 11px;
          color: #166534;
          font-weight: 500;
        }

        .resumen-item .value {
          font-size: 16px;
          font-weight: 600;
          color: #166534;
        }

        .resumen-item .value.success {
          color: #10B981;
        }

        .resumen-item .value.warning {
          color: #F59E0B;
        }

        .modal-body h4 {
          margin: 0 0 14px 0;
          font-size: 15px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .movimientos-container {
          display: flex;
          flex-direction: column;
          gap: 14px;
          max-height: 400px;
          overflow-y: auto;
          padding-right: 8px;
        }

        .movimiento-card {
          padding: 14px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
        }

        .movimiento-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #F3F4F6;
        }

        .movimiento-header strong {
          font-size: 13px;
          color: #1A1A1A;
        }

        .movimiento-header small {
          font-size: 11px;
          color: #6B7280;
        }

        .cantidad-tag {
          padding: 4px 10px;
          background: #DBEAFE;
          color: #1E40AF;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }

        .stock-existente {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 10px;
          background: #F0FDF4;
          border: 1px solid #BBF7D0;
          border-radius: 6px;
          font-size: 12px;
          color: #166534;
        }

        .stock-existente svg {
          color: #10B981;
        }

        .movimiento-detalle {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .origen-section,
        .destino-section,
        .manual-inputs-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .manual-inputs-section {
          grid-column: 1 / -1;
        }

        .origen-section h5,
        .destino-section h5,
        .manual-inputs-section h5 {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 0 0 8px 0;
          font-size: 11px;
          font-weight: 600;
          color: #6B7280;
          text-transform: uppercase;
        }

        .ubicacion-origen,
        .ubicacion-destino {
          padding: 10px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
        }

        .ubicacion-codigo {
          display: inline-block;
          padding: 3px 8px;
          background: #DBEAFE;
          color: #1E40AF;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .ubicacion-codigo.picking {
          background: #D1FAE5;
          color: #065F46;
        }

        .stock-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 10px;
          color: #6B7280;
          margin-top: 6px;
        }

        .stock-info .mover {
          color: #F59E0B;
          font-weight: 600;
        }

        .cantidad-mover {
          display: block;
          margin-top: 6px;
          font-size: 11px;
          color: #10B981;
          font-weight: 600;
        }

        .manual-inputs {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 12px;
          background: #FFFBEB;
          border: 1px solid #FDE047;
          border-radius: 6px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .input-group label {
          font-size: 10px;
          font-weight: 600;
          color: #92400E;
          text-transform: uppercase;
        }

        .input-group input {
          padding: 6px;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          font-size: 13px;
        }

        .nivel-selector {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .nivel-selector label {
          font-size: 11px;
          font-weight: 600;
          color: #6B7280;
        }

        .nivel-selector select {
          padding: 6px;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          font-size: 13px;
          background: white;
        }

        .movimiento-nota {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 10px;
          background: #EFF6FF;
          border: 1px solid #BFDBFE;
          border-radius: 6px;
          font-size: 12px;
          color: #1E40AF;
        }

        .download-examples {
          margin-bottom: 18px;
          padding: 14px;
          background: #F9FAFB;
          border-radius: 6px;
        }

        .download-examples p {
          margin: 0 0 10px 0;
          font-size: 13px;
          color: #1A1A1A;
        }

        .example-buttons {
          display: flex;
          gap: 10px;
        }

        .btn-example {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          color: #3B82F6;
          font-size: 12px;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-example:hover {
          background: #F9FAFB;
          border-color: #3B82F6;
        }

        .btn-upload-file {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 32px;
          background: #F9FAFB;
          border: 2px dashed #D1D5DB;
          border-radius: 8px;
          color: #6B7280;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-upload-file:hover {
          border-color: #3B82F6;
          background: #EFF6FF;
          color: #3B82F6;
        }

        .parsed-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 10px;
          background: #D1FAE5;
          border-radius: 6px;
          font-size: 13px;
          color: #065F46;
        }

        .return-modal {
          max-width: 550px;
        }

        .return-product-info {
          display: flex;
          gap: 14px;
          margin-bottom: 20px;
          padding: 14px;
          background: #F9FAFB;
          border-radius: 6px;
        }

        .return-product-info .product-image-cell {
          width: 60px;
          height: 60px;
          flex-shrink: 0;
        }

        .return-product-info .product-image-cell img,
        .return-product-info .no-image {
          width: 60px;
          height: 60px;
        }

        .return-product-info .product-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .return-product-info .product-details strong {
          font-size: 14px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .return-product-info .product-details span {
          font-size: 12px;
          color: #64748B;
        }

        .return-product-info .qty-info {
          font-size: 11px !important;
          color: #3B82F6 !important;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 18px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: #3B82F6;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: auto;
          cursor: pointer;
        }

        .checkbox-label span {
          font-size: 14px;
          color: #1A1A1A;
        }

        .info-box {
          padding: 12px;
          border-radius: 6px;
          font-size: 12px;
          margin-top: 12px;
        }

        .info-box svg {
          flex-shrink: 0;
        }

        .info-box.success {
          background: #D1FAE5;
          color: #065F46;
          border: 1px solid #A7F3D0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .info-box.success svg {
          color: #10B981;
        }

        .info-box.warning {
          background: #FEF3C7;
          color: #92400E;
          border: 1px solid #FDE68A;
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .info-box.warning svg {
          color: #F59E0B;
          margin-top: 2px;
        }

        .ubicacion-recomendada {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 10px;
          background: white;
          border: 1px solid #D1FAE5;
          border-radius: 6px;
          font-size: 13px;
          color: #065F46;
        }

        .ubicacion-recomendada strong {
          color: #10B981;
        }

        .lotes-summary {
          margin-top: 16px;
          padding: 12px;
          background: #F9FAFB;
          border-radius: 6px;
        }

        .lotes-summary h4 {
          margin: 0 0 10px 0;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }

        .lote-summary-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          margin-bottom: 6px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          font-size: 12px;
          color: #64748B;
        }

        .lote-summary-item:last-child {
          margin-bottom: 0;
        }

        .lote-summary-item svg {
          color: #3B82F6;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .filters-bar {
            flex-direction: column;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .totals-grid {
            flex-direction: column;
            align-items: flex-start;
          }

          .movimiento-detalle {
            grid-template-columns: 1fr;
          }

          .manual-inputs {
            grid-template-columns: 1fr;
          }

          .export-options {
            grid-template-columns: 1fr;
          }

          .modal-large {
            max-width: 95%;
          }
        }


      `}</style>
    </div>
  )
}

export default OrderManagement